import React, { Component } from "react";
import ReactDOM from "react-dom";
import { Button, Spinner, ProgressBar } from "react-bootstrap";
import firebase from "firebase";
import DB_PATH from "../../../../../../database/DB_PATH";
import { uploadMedia } from "../../../../../../database/media/cloudflareMedia";
import { AudioFile, BrollSelection, BrollOption, PexelsVideo, PixabayVideo, VideoSource, FreeFlowSegment, FreeFlowClipVariation, ReelMode } from "./types";
import {
  createOpenAIChatCompletion,
  createOpenAIImageGeneration,
} from "../../../../../../services/openaiFunctions";
import {
  listenToVideoLibrary,
  uploadVideoLibraryItem,
  removeVideoLibraryItem,
  backfillVideoLibraryThumbnail,
  setVideoLibraryItemFolder,
} from "../../../../../../database/community/videoLibrary";
import { VideoLibraryItem } from "../../../../../../types/Community";

type Props = {
  audioFiles: AudioFile[];
  text: string;
  pexelsApiKey: string;
  pixabayApiKey: string;
  jamendoClientId: string;
  geminiApiKey: string;
  stepId: string;
  communityId?: string;
  setFieldValue?: (field: string, value: any) => void;
  videoURL?: string; // Existing video URL from the database
};

type WorkflowStep = "idle" | "finding" | "selecting" | "rendering" | "complete";

type State = {
  isGeneratingReel: boolean;
  isRenderingReel: boolean;
  reelProgress: string;
  progressPercent: number;
  brollSelections: BrollSelection[];
  renderedVideoUrl: string | null;
  cloudVideoUrl: string | null; // URL from cloud storage after upload
  isConvertingToMp4: boolean;
  hasConfirmedBrolls: boolean;
  backgroundMusicUrl: string | null;
  searchInputs: string[];
  aiPromptInputs: string[];
  searchLoadingIndex: number | null;
  currentStep: WorkflowStep;
  expandedBrollIndex: number | null;
  videoSource: VideoSource;
  isCreatingNewReel: boolean; // Flag to bypass existing video and show creation UI
  reelMode: ReelMode;
  freeFlowSegments: FreeFlowSegment[];
  freeFlowCurrentStep: "composing" | "rendering" | "complete";
  isGeneratingImages: boolean;
  nanoBananaTheme: string;
  imageProvider: "nanobanana" | "dalle";
  aiGeneratingIndex: number | null;
  videoLibrary: VideoLibraryItem[];
  isLibraryOpen: boolean;
  libraryUploads: Array<{ id: string; name: string; percent: number }>;
  playingLibraryItemId: string | null;
  isBackfillingThumbnails: boolean;
  backfillProgress: { done: number; total: number };
  selectedLibraryFolder: string | null; // null = All, "" = Unsorted
  folderPickerForItemId: string | null;
  selectedLibraryItemIds: Record<string, true>;
  bulkFolderPickerOpen: boolean;
  isDraggingOverLibrary: boolean;
  librarySearch: string;
  librarySortBy: "newest" | "oldest" | "name" | "usage";
  expandedVariationsForSegId: string | null;
  clipPickerForSegId: string | null;
  draggingSegmentId: string | null;
  dropTargetSegmentId: string | null;
  freeflowBuilderMode: "combinations" | "manual";
  combinationPools: Array<{
    id: string;
    folder: string;
    durationSec: number;
    caption: string;
  }>;
  batchCount: number;
  isBatchRendering: boolean;
  batchProgress: { done: number; total: number };
  batchCancelRequested: boolean;
  batchResults: Array<{ url: string; createdAt: string; sizeBytes?: number; ext?: "mp4" | "webm" }>;
};

// Styles
const styles = {
  container: {
    marginTop: 24,
    background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
    borderRadius: 16,
    padding: 3,
  },
  innerContainer: {
    background: "#ffffff",
    borderRadius: 14,
    padding: 24,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  },
  headerIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "var(--color-surface-inset)",
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: "#6b7280",
    margin: 0,
    marginTop: 2,
  },
  stepIndicator: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 24,
    position: "relative" as const,
  },
  stepLine: {
    position: "absolute" as const,
    top: 20,
    left: "10%",
    right: "10%",
    height: 3,
    background: "#e5e7eb",
    zIndex: 0,
  },
  stepLineActive: {
    position: "absolute" as const,
    top: 20,
    left: "10%",
    height: 3,
    background: "linear-gradient(90deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
    zIndex: 1,
    transition: "width 0.3s ease",
  },
  step: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    zIndex: 2,
    flex: 1,
  },
  stepCircle: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 600,
    transition: "all 0.3s ease",
  },
  stepCircleInactive: {
    background: "#f3f4f6",
    color: "#9ca3af",
    border: "2px solid #e5e7eb",
  },
  stepCircleActive: {
    background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
    color: "#fff",
    border: "none",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
  },
  stepCircleComplete: {
    background: "#10b981",
    color: "#fff",
    border: "none",
  },
  stepLabel: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 500,
    color: "#6b7280",
    textAlign: "center" as const,
  },
  stepLabelActive: {
    color: "var(--color-accent)",
    fontWeight: 600,
  },
  emptyState: {
    textAlign: "center" as const,
    padding: "40px 20px",
    background: "#fafafa",
    borderRadius: 12,
    border: "2px dashed #e5e7eb",
  },
  emptyStateIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 14,
    color: "#6b7280",
    marginBottom: 0,
  },
  primaryButton: {
    background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
    border: "none",
    borderRadius: 10,
    padding: "12px 24px",
    fontSize: 15,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  progressCard: {
    background: "#f8fafc",
    borderRadius: 12,
    padding: 20,
    marginTop: 20,
    border: "1px solid #e2e8f0",
  },
  progressText: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 12,
    fontSize: 14,
    color: "#475569",
  },
  brollGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 16,
    marginTop: 20,
  },
  brollCard: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    transition: "box-shadow 0.2s, transform 0.2s",
  },
  brollCardHeader: {
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
  },
  brollCardHeaderTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  brollNumber: {
    background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
    color: "#fff",
    width: 28,
    height: 28,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
  },
  brollTag: {
    background: "var(--color-accent-subtle)",
    color: "var(--color-accent-active)",
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
  },
  brollTextPreview: {
    fontSize: 12,
    color: "#64748b",
    marginTop: 8,
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 4,
    WebkitBoxOrient: "vertical" as const,
    overflow: "hidden",
  },
  brollCardBody: {
    padding: 16,
  },
  thumbnailCarousel: {
    display: "flex",
    gap: 10,
    overflowX: "auto" as const,
    paddingBottom: 8,
    scrollSnapType: "x mandatory",
  },
  thumbnailItem: {
    flexShrink: 0,
    width: 120,
    scrollSnapAlign: "start",
    cursor: "pointer",
    borderRadius: 8,
    overflow: "hidden",
    transition: "transform 0.2s, box-shadow 0.2s",
    border: "2px solid transparent",
  },
  thumbnailItemSelected: {
    border: "2px solid var(--color-accent)",
    boxShadow: "0 0 0 2px rgba(102, 126, 234, 0.2)",
  },
  thumbnailVideo: {
    width: 120,
    height: 160,
    objectFit: "cover" as const,
    background: "#000",
    display: "block",
  },
  thumbnailLabel: {
    background: "rgba(0,0,0,0.7)",
    color: "#fff",
    fontSize: 10,
    padding: "4px 6px",
    textAlign: "center" as const,
  },
  searchRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap" as const,
    alignItems: "stretch",
    marginTop: 12,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  promptTextarea: {
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    resize: "vertical" as const,
    minHeight: 84,
    lineHeight: 1.45,
    boxSizing: "border-box" as const,
  },
  uploadLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    borderRadius: 8,
    fontSize: 12,
    color: "#64748b",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  actionBar: {
    display: "flex",
    gap: 12,
    marginTop: 24,
    paddingTop: 20,
    borderTop: "1px solid #e5e7eb",
  },
  previewSection: {
    marginTop: 24,
    background: "#f8fafc",
    borderRadius: 16,
    padding: 24,
    border: "1px solid #e2e8f0",
  },
  previewHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  previewTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--color-surface-inset)",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  previewContent: {
    display: "flex",
    gap: 24,
    flexWrap: "wrap" as const,
  },
  previewVideo: {
    width: 220,
    height: 391,
    borderRadius: 16,
    background: "#000",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
  },
  previewInfo: {
    flex: 1,
    minWidth: 200,
  },
  previewInfoItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 0",
    borderBottom: "1px solid #e5e7eb",
  },
  previewInfoIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    background: "var(--color-accent-subtle)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  successButton: {
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    border: "none",
    borderRadius: 10,
    padding: "12px 24px",
    fontSize: 15,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
  },
};

class StockFootageReelGenerator extends Component<Props, State> {
  state: State = {
    isGeneratingReel: false,
    isRenderingReel: false,
    reelProgress: "",
    progressPercent: 0,
    brollSelections: [],
    renderedVideoUrl: null,
    cloudVideoUrl: null,
    isConvertingToMp4: false,
    hasConfirmedBrolls: false,
    backgroundMusicUrl: null,
    searchInputs: [],
    aiPromptInputs: [],
    searchLoadingIndex: null,
    currentStep: "idle",
    expandedBrollIndex: null,
    videoSource: "pexels",
    isCreatingNewReel: false,
    reelMode: "auto",
    freeFlowSegments: [],
    freeFlowCurrentStep: "composing",
    isGeneratingImages: false,
    nanoBananaTheme: "",
    imageProvider: "nanobanana",
    aiGeneratingIndex: null,
    videoLibrary: [],
    isLibraryOpen: false,
    libraryUploads: [],
    playingLibraryItemId: null,
    isBackfillingThumbnails: false,
    backfillProgress: { done: 0, total: 0 },
    selectedLibraryFolder: null,
    folderPickerForItemId: null,
    selectedLibraryItemIds: {},
    bulkFolderPickerOpen: false,
    isDraggingOverLibrary: false,
    librarySearch: "",
    librarySortBy: "newest",
    expandedVariationsForSegId: null,
    clipPickerForSegId: null,
    draggingSegmentId: null,
    dropTargetSegmentId: null,
    freeflowBuilderMode: "combinations",
    combinationPools: [],
    batchCount: 8,
    isBatchRendering: false,
    batchProgress: { done: 0, total: 0 },
    batchCancelRequested: false,
    batchResults: [],
  };

  private unsubscribeVideoLibrary: (() => void) | null = null;

  componentDidMount() {
    const { communityId } = this.props;
    if (communityId) {
      this.unsubscribeVideoLibrary = listenToVideoLibrary(communityId, (items) => {
        this.setState({ videoLibrary: items });
      });
    }
    this.ensureDefaultCombinationPools();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.communityId !== this.props.communityId) {
      if (this.unsubscribeVideoLibrary) {
        this.unsubscribeVideoLibrary();
        this.unsubscribeVideoLibrary = null;
      }
      if (this.props.communityId) {
        this.unsubscribeVideoLibrary = listenToVideoLibrary(this.props.communityId, (items) => {
          this.setState({ videoLibrary: items });
        });
      } else {
        this.setState({ videoLibrary: [] });
      }
    }
  }

  componentWillUnmount() {
    if (this.unsubscribeVideoLibrary) {
      this.unsubscribeVideoLibrary();
      this.unsubscribeVideoLibrary = null;
    }
  }

  loadCommunityStylesForPrompts = async (): Promise<{ visualStyle: string; writingStyle: string }> => {
    const { communityId } = this.props;
    if (!communityId) {
      return { visualStyle: "", writingStyle: "" };
    }

    try {
      const snapshot = await firebase.firestore().collection(DB_PATH.COMMUNITY).doc(communityId).get();
      const data = snapshot.data();
      return {
        visualStyle: data?.visualStyle || "",
        writingStyle: data?.writingStyle || "",
      };
    } catch (error) {
      console.error("Error loading community styles for prompts:", error);
      return { visualStyle: "", writingStyle: "" };
    }
  };

  buildFallbackStyledPrompt = (
    segment: string,
    visualStyle: string,
    writingStyle: string
  ): string => {
    const visualPrefix = visualStyle?.trim()
      ? `Visual style: ${visualStyle.trim()}. `
      : "";
    const writingPrefix = writingStyle?.trim()
      ? `Mood inspired by writing style: ${writingStyle.trim()}. `
      : "";

    return `${visualPrefix}${writingPrefix}Create a portrait 9:16 cinematic scene that clearly represents this spoken concept: ${segment}. Show one main subject performing a concrete action that communicates the idea in a way viewers understand instantly, with an environment and props that reinforce the meaning. Use dynamic composition, strong depth, dramatic lighting, and emotionally coherent color grading. No text, letters, logos, or typography in the image.`;
  };

  generateStyledImagePrompts = async (
    segments: string[],
    visualStyle: string,
    writingStyle: string
  ): Promise<string[]> => {
    if (!segments.length) return [];

    const cleanSegments = segments.map((segment) => segment.trim());

    const visualStyleNote = visualStyle?.trim()
      ? `Community visual style: ${visualStyle.trim()}.`
      : "No specific visual style provided.";
    const writingStyleNote = writingStyle?.trim()
      ? `Community writing style/tone: ${writingStyle.trim()}.`
      : "No specific writing style provided.";

    try {
      const data = await createOpenAIChatCompletion<any>({
        model: "gpt-5-nano",
        messages: [
          {
            role: "system",
            content: `You create detailed AI image prompts for social media reels.
Generate exactly one prompt per input segment, preserving order.
Each prompt must:
- be 4-6 sentences (not 1-2)
- clearly represent the specific spoken concept in the segment (not generic visuals)
- include: main subject, concrete action, setting, props, and emotional tone
- include cinematic direction: framing/camera perspective, depth, lighting, and color mood
- include one clear symbolic visual element that reinforces the segment meaning
- use portrait 9:16 framing suitable for reels
- incorporate style and mood guidance from the provided community styles
- stay in a single coherent scene, visually rich but physically plausible
- never include text, letters, logos, captions, or typography in the image
Return ONLY a JSON array of strings with the same length as the input segments.`,
          },
          {
            role: "user",
            content: JSON.stringify({
              styles: {
                visualStyle: visualStyleNote,
                writingStyle: writingStyleNote,
              },
              segments: cleanSegments,
            }),
          },
        ]
      });
      const content = data?.choices?.[0]?.message?.content?.trim() || "[]";
      const parsed = JSON.parse(content);

      if (!Array.isArray(parsed)) {
        throw new Error("Styled prompt response was not an array");
      }

      return cleanSegments.map((segment, index) => {
        const candidate = typeof parsed[index] === "string" ? parsed[index].trim() : "";
        return candidate || this.buildFallbackStyledPrompt(segment, visualStyle, writingStyle);
      });
    } catch (error) {
      console.error("Error generating styled image prompts:", error);
      return cleanSegments.map((segment) =>
        this.buildFallbackStyledPrompt(segment, visualStyle, writingStyle)
      );
    }
  };

  // Extract keyword for a single paragraph
  extractKeywordFromParagraph = async (paragraph: string): Promise<string> => {
    try {
      const data = await createOpenAIChatCompletion<any>({
        model: "gpt-5-nano",
        messages: [
          {
            role: "system",
            content: "Extract 1 visual keyword from the text that would work well for finding stock footage. Return only ONE word or short phrase (2-3 words max). Focus on concrete, visual concepts (nature, emotions, activities, objects). Avoid abstract concepts.",
          },
          { role: "user", content: paragraph },
        ]
      });
      return data?.choices?.[0]?.message?.content?.trim() || "nature";
    } catch (error) {
      console.error("Error extracting keyword:", error);
      return "nature";
    }
  };

  searchPexelsVideos = async (query: string): Promise<PexelsVideo[]> => {
    try {
      const response = await fetch(
        `https://api.pexels.com/videos/search?query=${encodeURIComponent(query)}&per_page=5&orientation=portrait`,
        {
          headers: {
            Authorization: this.props.pexelsApiKey,
          },
        }
      );

      if (!response.ok) throw new Error("Pexels API request failed");

      const data = await response.json();
      return data.videos || [];
    } catch (error) {
      console.error("Error searching Pexels videos:", error);
      return [];
    }
  };

  searchPixabayVideos = async (query: string): Promise<PixabayVideo[]> => {
    try {
      const response = await fetch(
        `https://pixabay.com/api/videos/?key=${this.props.pixabayApiKey}&q=${encodeURIComponent(query)}&per_page=5&video_type=film`
      );

      if (!response.ok) throw new Error("Pixabay API request failed");

      const data = await response.json();
      return data.hits || [];
    } catch (error) {
      console.error("Error searching Pixabay videos:", error);
      return [];
    }
  };

  buildOptionsFromPixabayVideos = (videos: PixabayVideo[], max: number = 3): BrollOption[] => {
    const options: BrollOption[] = [];
    for (const vid of videos) {
      if (options.length >= max) break;
      // Prefer large, then medium, then small
      const videoFile = vid.videos.large || vid.videos.medium || vid.videos.small;
      if (videoFile?.url) {
        options.push({
          videoUrl: videoFile.url,
          thumbnail: `https://i.vimeocdn.com/video/${vid.picture_id}_295x166.jpg`,
          duration: vid.duration,
        });
      }
    }
    return options;
  };

  searchVideos = async (query: string): Promise<BrollOption[]> => {
    const { videoSource } = this.state;
    if (videoSource === "pixabay") {
      const videos = await this.searchPixabayVideos(query);
      return this.buildOptionsFromPixabayVideos(videos);
    } else {
      const videos = await this.searchPexelsVideos(query);
      return this.buildOptionsFromVideos(videos);
    }
  };

  // Generate image using Gemini API
  generateNanoBananaImage = async (prompt: string): Promise<string> => {
    const { geminiApiKey } = this.props;
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-image:generateContent?key=${geminiApiKey}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ parts: [{ text: `${prompt} IMPORTANT: Do not include any text, letters, words, numbers, typography, captions, watermarks, or writing of any kind in the image. Pure visual only.` }] }],
          generationConfig: { responseModalities: ["TEXT", "IMAGE"] },
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Gemini API error: ${response.status} - ${JSON.stringify(errorData)}`);
    }

    const data = await response.json();
    const parts = data?.candidates?.[0]?.content?.parts || [];
    for (const part of parts) {
      if (part.inlineData) {
        return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
      }
    }
    throw new Error("No image returned from Gemini API");
  };

  // Generate image using OpenAI GPT Image API
  generateDalleImage = async (prompt: string): Promise<string> => {
    const noTextPrompt = `${prompt} IMPORTANT: Do not include any text, letters, words, numbers, typography, captions, watermarks, or writing of any kind in the image. Pure visual only.`;
    const data = await createOpenAIImageGeneration<any>({
      model: "gpt-image-1",
      prompt: noTextPrompt,
      n: 1,
      size: "1024x1536",
      quality: "high",
      output_format: "png",
    });
    const b64 = data?.data?.[0]?.b64_json;
    if (!b64) throw new Error("No image returned from GPT Image API");
    return `data:image/png;base64,${b64}`;
  };

  // Generate image using the selected provider
  generateImageWithProvider = async (prompt: string): Promise<string> => {
    if (this.state.imageProvider === "dalle") {
      return this.generateDalleImage(prompt);
    }
    return this.generateNanoBananaImage(prompt);
  };

  // Generate AI image for a specific B-roll selection
  handleAiGenerateForSelection = async (selectionIndex: number) => {
    const { brollSelections, aiPromptInputs } = this.state;
    const broll = brollSelections[selectionIndex];
    if (!broll) return;

    this.setState({ aiGeneratingIndex: selectionIndex });

    try {
      const themePrefix = this.state.nanoBananaTheme ? `${this.state.nanoBananaTheme}. ` : "";
      const basePrompt = (aiPromptInputs[selectionIndex] || broll.textSegment || "A beautiful visual scene").trim();
      const prompt = `${themePrefix}${basePrompt}. Portrait orientation 9:16 aspect ratio, high quality.`;
      const imageUrl = await this.generateImageWithProvider(prompt);

      const option: BrollOption = {
        videoUrl: imageUrl,
        thumbnail: imageUrl,
        duration: 5,
        label: "AI Generated",
        isAiGenerated: true,
      };

      this.setState((prev) => {
        const nextSelections = [...prev.brollSelections];
        if (!nextSelections[selectionIndex]) return prev;
        nextSelections[selectionIndex] = {
          ...nextSelections[selectionIndex],
          options: [option, ...nextSelections[selectionIndex].options],
          selectedIndex: 0,
        };
        return { ...prev, brollSelections: nextSelections, aiGeneratingIndex: null };
      });
    } catch (error) {
      console.error("Error generating AI image:", error);
      this.setState({ aiGeneratingIndex: null, reelProgress: "Failed to generate AI image." });
    }
  };

  handleGenerateAiForAllSegments = async () => {
    const { brollSelections } = this.state;
    if (brollSelections.length === 0) return;

    this.setState({
      isGeneratingImages: true,
      reelProgress: `Generating AI image 1/${brollSelections.length}...`,
      progressPercent: 0,
    });

    try {
      for (let i = 0; i < brollSelections.length; i++) {
        this.setState({
          reelProgress: `Generating AI image ${i + 1}/${brollSelections.length}...`,
          progressPercent: Math.floor((i / brollSelections.length) * 100),
        });

        await this.handleAiGenerateForSelection(i);
      }

      this.setState({
        reelProgress: `Generated AI images for ${brollSelections.length} segment(s).`,
        progressPercent: 100,
      });
    } catch (error) {
      console.error("Error generating AI images for all segments:", error);
      this.setState({ reelProgress: "Failed to generate AI images for all segments." });
    } finally {
      this.setState({ isGeneratingImages: false, aiGeneratingIndex: null });
    }
  };

  handleAiPromptChange = (selectionIndex: number, value: string) => {
    this.setState((prev) => {
      const next = [...prev.aiPromptInputs];
      next[selectionIndex] = value;
      return { ...prev, aiPromptInputs: next };
    });
  };

  downloadImage = async (imageUrl: string, fileName: string) => {
    try {
      if (imageUrl.startsWith("data:image")) {
        const a = document.createElement("a");
        a.href = imageUrl;
        a.download = fileName;
        a.click();
        return;
      }

      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error("Error downloading image:", error);
      this.setState({ reelProgress: "Could not download image. Please try again." });
    }
  };

  // Get background music based on mood - using Jamendo API
  searchJamendoMusic = async (query: string): Promise<string | null> => {
    try {
      // Map common mood keywords to Jamendo tags
      const moodToTags: Record<string, string> = {
        peaceful: "relaxing+calm",
        meditation: "meditation+ambient",
        inspiring: "inspiring+uplifting",
        motivational: "energetic+positive",
        calm: "calm+soft",
        ambient: "ambient+atmospheric",
        uplifting: "uplifting+happy",
        relaxing: "relaxing+chillout",
        piano: "piano+instrumental",
        acoustic: "acoustic+folk",
        gentle: "soft+mellow",
        nature: "nature+ambient",
      };

      // Find matching tags or use query directly
      const queryLower = query.toLowerCase();
      let tags = queryLower.replace(/\s+/g, "+");

      for (const [mood, moodTags] of Object.entries(moodToTags)) {
        if (queryLower.includes(mood)) {
          tags = moodTags;
          break;
        }
      }

      const response = await fetch(
        `https://api.jamendo.com/v3.0/tracks/?client_id=${this.props.jamendoClientId}&format=json&limit=10&fuzzytags=${tags}&vocalinstrumental=instrumental&include=musicinfo&order=popularity_total`,
      );

      if (!response.ok) {
        console.error("Jamendo API request failed:", response.status);
        return null;
      }

      const data = await response.json();
      const tracks = data?.results || [];

      if (tracks.length === 0) {
        console.log("No tracks found for query:", query, "- trying fallback");
        // Fallback search with generic relaxing tag
        const fallbackResponse = await fetch(
          `https://api.jamendo.com/v3.0/tracks/?client_id=${this.props.jamendoClientId}&format=json&limit=10&fuzzytags=relaxing+ambient&vocalinstrumental=instrumental&order=popularity_total`,
        );

        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          const fallbackTracks = fallbackData?.results || [];
          if (fallbackTracks.length > 0) {
            const randomTrack = fallbackTracks[Math.floor(Math.random() * fallbackTracks.length)];
            console.log(`Selected fallback Jamendo track: ${randomTrack.name} by ${randomTrack.artist_name}`);
            return randomTrack.audiodownload || randomTrack.audio;
          }
        }
        return null;
      }

      // Pick a random track from results for variety
      const randomTrack = tracks[Math.floor(Math.random() * tracks.length)];
      console.log(`Selected Jamendo track: ${randomTrack.name} by ${randomTrack.artist_name}`);
      // Use audiodownload URL which has better CORS support than audio streaming URL
      return randomTrack.audiodownload || randomTrack.audio;
    } catch (error) {
      console.error("Error searching Jamendo music:", error);
      return null;
    }
  };

  // Extract mood/genre keywords for music search using OpenAI
  extractMusicKeywords = async (text: string): Promise<string> => {
    try {
      const data = await createOpenAIChatCompletion<any>({
        model: "gpt-4o-mini",
        messages: [
          {
            role: "system",
            content: "Based on the text, suggest ONE music search term (1-2 words) that would work as background music. Choose from moods/genres like: peaceful, meditation, inspiring, calm, ambient, motivational, relaxing, uplifting, gentle, soft piano, acoustic, nature sounds. Return only the search term, nothing else.",
          },
          { role: "user", content: text },
        ],
        temperature: 0.3,
      });
      return data?.choices?.[0]?.message?.content?.trim() || "peaceful ambient";
    } catch (error) {
      console.error("Error extracting music keywords:", error);
      return "peaceful ambient";
    }
  };

  // Get audio duration from URL (works without CORS)
  getAudioDuration = (url: string): Promise<number> => {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      audio.onloadedmetadata = () => resolve(audio.duration);
      audio.onerror = () => reject(new Error("Failed to load audio metadata"));
      audio.src = url;
    });
  };

  wait = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  withRetryCacheBust = (url: string, attempt: number): string => {
    if (attempt <= 1) return url;
    if (url.startsWith("blob:") || url.startsWith("data:")) return url;
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}retryAttempt=${attempt}&t=${Date.now()}`;
  };

  loadImageWithRetries = async (url: string, label: string, maxAttempts: number = 3): Promise<HTMLImageElement> => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const image = new Image();
          image.crossOrigin = "anonymous";

          const timeoutId = window.setTimeout(() => {
            reject(new Error(`${label} timed out while loading.`));
          }, 12000);

          image.onload = () => {
            window.clearTimeout(timeoutId);
            if (!image.naturalWidth || !image.naturalHeight) {
              reject(new Error(`${label} loaded but has invalid dimensions.`));
              return;
            }
            resolve(image);
          };

          image.onerror = () => {
            window.clearTimeout(timeoutId);
            reject(new Error(`${label} failed to load.`));
          };

          image.src = this.withRetryCacheBust(url, attempt);
        });

        return img;
      } catch (error) {
        if (attempt < maxAttempts) {
          await this.wait(250 * attempt);
        }
      }
    }

    throw new Error(`${label} failed after ${maxAttempts} attempts. Please choose another option.`);
  };

  loadVideoWithRetries = async (url: string, label: string, maxAttempts: number = 3): Promise<HTMLVideoElement> => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const video = await new Promise<HTMLVideoElement>((resolve, reject) => {
          const el = document.createElement("video");
          el.crossOrigin = "anonymous";
          el.muted = true;
          el.playsInline = true;
          el.preload = "auto";

          const timeoutId = window.setTimeout(() => {
            reject(new Error(`${label} timed out while loading.`));
          }, 15000);

          const onReady = () => {
            if (el.readyState >= 2 && el.videoWidth > 0 && el.videoHeight > 0) {
              window.clearTimeout(timeoutId);
              resolve(el);
            }
          };

          el.onloadeddata = onReady;
          el.oncanplay = onReady;
          el.onerror = () => {
            window.clearTimeout(timeoutId);
            reject(new Error(`${label} failed to load.`));
          };

          el.src = this.withRetryCacheBust(url, attempt);
          el.load();
        });

        return video;
      } catch (error) {
        if (attempt < maxAttempts) {
          await this.wait(300 * attempt);
        }
      }
    }

    throw new Error(`${label} failed after ${maxAttempts} attempts. Please choose another option.`);
  };

  drawAppName = (
    ctx: CanvasRenderingContext2D,
    canvasWidth: number,
    canvasHeight: number,
    appName: string = "Tribed"
  ) => {
    const padding = 60;
    const fontSize = 48;
    const centerX = canvasWidth / 2;
    const y = canvasHeight - padding;

    ctx.save();

    // Elegant script-style font with fallbacks
    ctx.font = `italic 600 ${fontSize}px "Georgia", "Times New Roman", serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const textWidth = ctx.measureText(appName).width;

    // Subtle pill-shaped backdrop for legibility
    const boxPaddingH = 30;
    const boxPaddingV = 16;
    const boxWidth = textWidth + boxPaddingH * 2;
    const boxHeight = fontSize + boxPaddingV;

    ctx.globalAlpha = 0.6;
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.beginPath();
    ctx.roundRect(
      centerX - boxWidth / 2,
      y - boxHeight / 2,
      boxWidth,
      boxHeight,
      boxHeight / 2
    );
    ctx.fill();

    // Draw app name with slight shadow for depth
    ctx.globalAlpha = 1;
    ctx.shadowColor = "rgba(0, 0, 0, 0.4)";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 2;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillText(appName, centerX, y);

    ctx.restore();
  };

  loadCommunityName = async (): Promise<string> => {
    const { communityId } = this.props;
    if (!communityId) return "Tribed";

    try {
      const snapshot = await firebase.firestore().collection(DB_PATH.COMMUNITY).doc(communityId).get();
      const data = snapshot.data();
      return data?.title || "Tribed";
    } catch (error) {
      console.error("Error loading community name:", error);
      return "Tribed";
    }
  };

  // Load script from URL
  loadScript = (url: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${url}"]`);
      if (existing) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = url;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${url}`));
      document.head.appendChild(script);
    });
  };

  // Convert WebM blob to MP4 using FFmpeg.wasm (loaded from CDN)
  convertToMp4 = async (
    input: Blob | string,
    opts: { silent?: boolean; onProgress?: (info: { stage: string; percent: number }) => void } = {}
  ): Promise<{ url: string; blob: Blob }> => {
    const { silent, onProgress } = opts;
    const report = (stage: string, percent: number) => {
      if (!silent) this.setState({ reelProgress: stage, progressPercent: percent });
      onProgress?.({ stage, percent });
    };

    if (!silent) this.setState({ isConvertingToMp4: true });
    report("Loading FFmpeg (first time may be slow)...", 5);

    // Simulated progress for better UX (FFmpeg progress callback is unreliable)
    let simulatedProgress = 30;
    const progressInterval = setInterval(() => {
      if (simulatedProgress < 85) {
        simulatedProgress += Math.random() * 3 + 1; // Random increment 1-4%
        report(`Converting to MP4... ${Math.floor(simulatedProgress)}%`, Math.floor(simulatedProgress));
      }
    }, 500);

    try {
      // Load FFmpeg from CDN (older version that works with more bundlers)
      await this.loadScript("https://cdn.jsdelivr.net/npm/@ffmpeg/ffmpeg@0.11.6/dist/ffmpeg.min.js");

      const { createFFmpeg, fetchFile } = (window as any).FFmpeg;

      report("Initializing video converter...", 15);

      // Track duration for progress calculation
      let totalDuration = 0;

      // Use single-threaded core (no SharedArrayBuffer required)
      const ffmpeg = createFFmpeg({
        log: true,
        corePath: "https://unpkg.com/@ffmpeg/core-st@0.11.1/dist/ffmpeg-core.js",
        mainName: "main",
        logger: ({ message }: { message: string }) => {
          // Parse duration from FFmpeg output: "Duration: 00:00:10.50"
          const durationMatch = message.match(/Duration:\s*(\d+):(\d+):(\d+\.\d+)/);
          if (durationMatch) {
            const hours = parseInt(durationMatch[1]);
            const minutes = parseInt(durationMatch[2]);
            const seconds = parseFloat(durationMatch[3]);
            totalDuration = hours * 3600 + minutes * 60 + seconds;
          }

          // Parse current time from FFmpeg output: "time=00:00:05.25"
          const timeMatch = message.match(/time=(\d+):(\d+):(\d+\.\d+)/);
          if (timeMatch && totalDuration > 0) {
            const hours = parseInt(timeMatch[1]);
            const minutes = parseInt(timeMatch[2]);
            const seconds = parseFloat(timeMatch[3]);
            const currentTime = hours * 3600 + minutes * 60 + seconds;
            const ratio = Math.min(currentTime / totalDuration, 1);
            const percent = 30 + Math.floor(ratio * 60); // 30% to 90%

            // Only update if we have real progress (overrides simulated)
            simulatedProgress = percent;
            report(`Converting to MP4... ${Math.floor(ratio * 100)}%`, percent);
          }
        }
      });

      if (!ffmpeg.isLoaded()) {
        report("Loading video encoder (one-time download)...", 20);
        await ffmpeg.load();
      }

      report("Preparing video data...", 25);

      // Accept either a Blob directly or a blob URL
      const webmBlob = input instanceof Blob ? input : await (await fetch(input)).blob();

      report("Converting to MP4... 0%", 30);

      // Write input file
      ffmpeg.FS("writeFile", "input.webm", await fetchFile(webmBlob));

      // Convert to MP4 with H.264 codec - using faster settings
      // IMPORTANT: Specify frame rate to prevent video slowdown during conversion
      await ffmpeg.run(
        "-r", "30",              // Input frame rate (must match canvas.captureStream fps)
        "-i", "input.webm",
        "-c:v", "libx264",
        "-preset", "ultrafast",  // Fastest encoding
        "-crf", "28",            // Acceptable quality, faster encoding
        "-r", "30",              // Output frame rate
        "-vsync", "cfr",         // Constant frame rate to prevent timing issues
        "-c:a", "aac",
        "-b:a", "128k",
        "-movflags", "+faststart",
        "-y",
        "output.mp4"
      );

      clearInterval(progressInterval);
      report("Finalizing...", 95);

      // Read output file
      const mp4Data = ffmpeg.FS("readFile", "output.mp4");
      const mp4Blob = new Blob([mp4Data.buffer], { type: "video/mp4" });
      const mp4Url = URL.createObjectURL(mp4Blob);

      // Cleanup
      ffmpeg.FS("unlink", "input.webm");
      ffmpeg.FS("unlink", "output.mp4");

      report("MP4 ready!", 100);

      return { url: mp4Url, blob: mp4Blob };
    } catch (error) {
      clearInterval(progressInterval);
      console.error("Error converting to MP4:", error);
      if (!silent) this.setState({ reelProgress: "MP4 conversion failed. Downloading WebM instead..." });
      throw error;
    } finally {
      clearInterval(progressInterval);
      if (!silent) this.setState({ isConvertingToMp4: false });
    }
  };

  // Download as WebM (instant, no conversion)
  downloadReelWebM = () => {
    const { renderedVideoUrl } = this.state;
    if (!renderedVideoUrl) return;

    const a = document.createElement("a");
    a.href = renderedVideoUrl;
    a.download = `reel-${Date.now()}.webm`;
    a.click();
  };

  // Download reel as MP4
  downloadReel = async () => {
    const { renderedVideoUrl } = this.state;
    const { communityId, stepId } = this.props;
    if (!renderedVideoUrl) return;

    try {
      // Convert WebM to MP4
      const { url: mp4Url, blob: mp4Blob } = await this.convertToMp4(renderedVideoUrl);

      const a = document.createElement("a");
      a.href = mp4Url;
      a.download = `reel-${Date.now()}.mp4`;
      a.click();

      if (this.state.reelMode !== "freeflow" && communityId && stepId) {
        // Keep progress visible during upload (skip for free flow mode)
        this.setState({ isConvertingToMp4: true });
        await this.uploadReelToStorage(mp4Blob, communityId, stepId);
        this.setState({ isConvertingToMp4: false });
      }

      // Clean up the MP4 blob URL after download
      setTimeout(() => URL.revokeObjectURL(mp4Url), 1000);
    } catch (error) {
      // Fallback to WebM if MP4 conversion fails
      const a = document.createElement("a");
      a.href = renderedVideoUrl;
      a.download = `reel-${Date.now()}.webm`;
      a.click();
    }
  };

  buildOptionsFromVideos = (videos: PexelsVideo[], max: number = 3): BrollOption[] => {
    const options: BrollOption[] = [];
    for (const vid of videos) {
      if (options.length >= max) break;
      const hdFile = vid.video_files.find((f) => f.quality === "hd" && f.height > f.width)
        || vid.video_files.find((f) => f.quality === "hd")
        || vid.video_files[0];
      if (hdFile?.link) {
        options.push({
          video: vid,
          videoUrl: hdFile.link,
          thumbnail: vid.image,
          duration: vid.duration,
        });
      }
    }
    return options;
  };

  uploadReelToStorage = async (blob: Blob, communityId: string, stepId: string) => {
    try {
      this.setState({ reelProgress: "Uploading reel to cloud...", progressPercent: 0 });
      const path = `${DB_PATH.VIDEO}/${communityId}/${stepId}/reel.mp4`;
      const uploaded = await uploadMedia({
        body: blob,
        kind: "video",
        contentType: blob.type || "video/mp4",
        fileName: "reel.mp4",
        firebasePath: path,
        onProgress: (fraction) => {
          const progress = fraction * 100;
          this.setState({
            reelProgress: `Uploading to cloud... ${Math.floor(progress)}%`,
            progressPercent: Math.floor(progress * 0.8),
          });
        },
      });

      this.setState({ reelProgress: "Getting download URL...", progressPercent: 85 });
      const downloadUrl = uploaded.url;

      this.setState({ reelProgress: "Saving to database...", progressPercent: 90 });

      // Update form state for immediate UI reflection
      if (this.props.setFieldValue) {
        this.props.setFieldValue("videoURL", downloadUrl);
      }

      // Persist video URL to Firestore
      try {
        const firestore = firebase.firestore();
        await firestore
          .collection(DB_PATH.PLAN_STEP)
          .doc(stepId)
          .set({ videoURL: downloadUrl }, { merge: true });
      } catch (err) {
        console.error("Error updating step in Firestore:", err);
        this.setState({ reelProgress: "Uploaded, but failed to save to database." });
        return;
      }

      this.setState({ reelProgress: "Reel saved successfully!", progressPercent: 100, cloudVideoUrl: downloadUrl });
    } catch (error) {
      console.error("Error uploading reel to storage:", error);
      this.setState({ reelProgress: "Error uploading reel. Please try again." });
    }
  };

  // Render video client-side using Canvas + MediaRecorder
  // Audio is played live through HTML Audio elements (no CORS needed)
  // Each video clip plays for brollDuration seconds
  renderVideoClientSide = async (
    videoUrls: string[],
    audioFiles: AudioFile[],
    brollDuration: number = 5, // seconds per B-roll clip
    backgroundMusicUrl: string | null = null, // Optional background music URL
    communityName: string = "Tribed", // Community name to display
    aiFlags: boolean[] = [] // Whether each URL is an AI-generated image
  ): Promise<string> => {
    // Create canvas for video rendering (9:16 portrait for reels)
    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1920;
    const ctx = canvas.getContext("2d")!;

    // Create audio context for capturing audio
    const audioContext = new AudioContext();
    const audioDestination = audioContext.createMediaStreamDestination();

    // Get total audio duration first
    this.setState({ reelProgress: "Calculating audio duration...", progressPercent: 5 });
    let totalAudioDuration = 0;
    const audioDurations: number[] = [];
    for (const file of audioFiles) {
      try {
        const duration = await this.getAudioDuration(file.url);
        audioDurations.push(duration);
        totalAudioDuration += duration;
        console.log(`Audio ${file.name}: ${duration}s`);
      } catch (e) {
        console.error("Error getting duration for:", file.name, e);
        audioDurations.push(0);
      }
    }

    console.log("Total audio duration:", totalAudioDuration);

    if (totalAudioDuration === 0) {
      throw new Error("Could not determine audio duration");
    }

    // Create audio elements and connect them to the audio context
    this.setState({ reelProgress: "Setting up audio...", progressPercent: 15 });
    const audioElements: HTMLAudioElement[] = [];

    for (const file of audioFiles) {
      const audio = new Audio();
      audio.crossOrigin = "anonymous"; // Required for Web Audio API to capture
      audio.src = file.url;
      audio.preload = "auto";

      // Wait for audio to be ready
      await new Promise<void>((resolve) => {
        audio.oncanplaythrough = () => resolve();
        audio.onerror = () => resolve();
        audio.load();
      });

      // Connect to audio context for recording with gain control
      const source = audioContext.createMediaElementSource(audio);
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 1.0; // Full volume for voice
      source.connect(gainNode);
      gainNode.connect(audioDestination);
      gainNode.connect(audioContext.destination); // Also play through speakers (optional)

      audioElements.push(audio);
    }

    // Setup background music if provided (optional - reel works without it)
    let backgroundMusicElement: HTMLAudioElement | null = null;
    if (backgroundMusicUrl) {
      this.setState({ reelProgress: "Setting up background music...", progressPercent: 20 });
      try {
        backgroundMusicElement = new Audio();
        backgroundMusicElement.crossOrigin = "anonymous";
        backgroundMusicElement.src = backgroundMusicUrl;
        backgroundMusicElement.loop = true;
        backgroundMusicElement.preload = "auto";

        // Wait for audio with timeout (5 seconds max)
        const loadResult = await Promise.race([
          new Promise<"loaded">((resolve) => {
            backgroundMusicElement!.oncanplaythrough = () => resolve("loaded");
          }),
          new Promise<"error">((resolve) => {
            backgroundMusicElement!.onerror = () => resolve("error");
          }),
          new Promise<"timeout">((resolve) => setTimeout(() => resolve("timeout"), 5000))
        ]);

        if (loadResult !== "loaded") {
          console.log("Background music skipped:", loadResult);
          backgroundMusicElement = null;
        }

        backgroundMusicElement?.load();
      } catch {
        console.log("Background music unavailable, continuing without it");
        backgroundMusicElement = null;
      }

      if (backgroundMusicElement) {
        try {
          const musicSource = audioContext.createMediaElementSource(backgroundMusicElement);
          const musicGainNode = audioContext.createGain();
          musicGainNode.gain.value = 0.02; // Subtle background music
          musicSource.connect(musicGainNode);
          musicGainNode.connect(audioDestination);
          musicGainNode.connect(audioContext.destination);
        } catch {
          console.log("Could not connect background music to audio context");
          backgroundMusicElement = null;
        }
      }
    }

    // Create video/image elements for each media URL
    this.setState({ reelProgress: "Loading media clips...", progressPercent: 30 });
    const mediaElements: (HTMLVideoElement | HTMLImageElement)[] = [];
    for (let i = 0; i < videoUrls.length; i++) {
      const url = videoUrls[i];
      const isAi = aiFlags[i] || false;
      const clipLabel = `Clip ${i + 1}`;

      if (isAi) {
        // Retry image loading before failing the whole render.
        const img = await this.loadImageWithRetries(url, clipLabel, 3);
        mediaElements.push(img);
      } else {
        // Retry video loading before failing the whole render.
        const video = await this.loadVideoWithRetries(url, clipLabel, 3);
        mediaElements.push(video);
      }
    }

    if (mediaElements.length === 0) {
      throw new Error("No media could be loaded");
    }

    // Setup MediaRecorder
    const canvasStream = canvas.captureStream(30);
    const audioTrack = audioDestination.stream.getAudioTracks()[0];
    if (audioTrack) {
      canvasStream.addTrack(audioTrack);
    }

    const mediaRecorder = new MediaRecorder(canvasStream, {
      mimeType: "video/webm;codecs=vp9",
      videoBitsPerSecond: 5000000,
    });

    const chunks: Blob[] = [];
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    // Start recording
    mediaRecorder.start(100);

    // Play audio files sequentially
    this.setState({ reelProgress: "Rendering video...", progressPercent: 40 });
    const startTime = performance.now();
    const totalDuration = totalAudioDuration * 1000; // ms
    const brollDurationMs = brollDuration * 1000; // Convert to ms
    let currentVideoIndex = 0;
    let lastBrollIndex = -1;

    // Start first audio
    if (audioElements.length > 0) {
      audioElements[0].play().catch(console.error);
    }

    // Start background music if available
    if (backgroundMusicElement) {
      backgroundMusicElement.play().catch(console.error);
    }

    // Setup audio chain - when one ends, play next
    for (let i = 0; i < audioElements.length - 1; i++) {
      audioElements[i].onended = () => {
        if (audioElements[i + 1]) {
          audioElements[i + 1].play().catch(console.error);
        }
      };
    }

    // Start first media element (only play if it's a video)
    const firstMedia = mediaElements[0];
    if (firstMedia instanceof HTMLVideoElement) {
      firstMedia.currentTime = 0;
      firstMedia.play().catch(() => {});
    }

    await new Promise<void>((resolve) => {
      const renderFrame = () => {
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / totalDuration, 1);

        // Update progress
        const renderPercent = 40 + Math.floor(progress * 55); // 40% to 95%
        if (Math.floor(progress * 20) > Math.floor((elapsed - 50) / totalDuration * 20)) {
          this.setState({ reelProgress: `Rendering video... ${Math.floor(progress * 100)}%`, progressPercent: renderPercent });
        }

        // Switch B-roll every brollDuration seconds
        const currentBrollIndex = Math.floor(elapsed / brollDurationMs);

        if (currentBrollIndex !== lastBrollIndex && currentBrollIndex < mediaElements.length) {
          lastBrollIndex = currentBrollIndex;
          const newMediaIndex = currentBrollIndex % mediaElements.length;

          if (newMediaIndex !== currentVideoIndex) {
            // Pause old video if it was a video
            const oldMedia = mediaElements[currentVideoIndex];
            if (oldMedia instanceof HTMLVideoElement) {
              oldMedia.pause();
            }
            currentVideoIndex = newMediaIndex;
            // Start new video if it's a video
            const newMedia = mediaElements[currentVideoIndex];
            if (newMedia instanceof HTMLVideoElement) {
              newMedia.currentTime = 0;
              newMedia.play().catch(() => {});
            }
          }
        }

        // Draw current media frame to canvas
        const media = mediaElements[currentVideoIndex];
        if (media instanceof HTMLImageElement) {
          // Ken Burns animation for still images (zoom + pan per B-roll segment)
          if (media.complete && media.naturalWidth > 0) {
            const rawSegmentProgress = brollDurationMs > 0
              ? (elapsed % brollDurationMs) / brollDurationMs
              : 0;
            const segmentProgress = rawSegmentProgress * rawSegmentProgress * (3 - 2 * rawSegmentProgress);
            const panPhase = segmentProgress * 2 - 1; // -1..1
            const wave = Math.sin(rawSegmentProgress * Math.PI * 2); // Adds extra kinetic feel

            const animationIndex = currentBrollIndex >= 0 ? currentBrollIndex : currentVideoIndex;
            const directions = [
              { x: -1, y: -0.35 },
              { x: 1, y: -0.2 },
              { x: -0.8, y: 0.45 },
              { x: 0.75, y: 0.3 },
            ];
            const direction = directions[Math.abs(animationIndex) % directions.length];

            const coverScale = Math.max(canvas.width / media.naturalWidth, canvas.height / media.naturalHeight);
            const zoomStart = 1.22 + (Math.abs(animationIndex) % 3) * 0.02;
            const zoomEnd = zoomStart + 0.26;
            const zoomPulse = Math.sin(segmentProgress * Math.PI) * 0.07;
            const zoom = zoomStart + (zoomEnd - zoomStart) * segmentProgress + zoomPulse;

            const w = media.naturalWidth * coverScale * zoom;
            const h = media.naturalHeight * coverScale * zoom;

            const maxPanX = Math.max(0, (w - canvas.width) / 2);
            const maxPanY = Math.max(0, (h - canvas.height) / 2);
            const targetPanX = direction.x * panPhase * (canvas.width * 0.18) + wave * (canvas.width * 0.03);
            const targetPanY = direction.y * panPhase * (canvas.height * 0.12) - wave * (canvas.height * 0.02);
            const panX = Math.max(-maxPanX, Math.min(maxPanX, targetPanX));
            const panY = Math.max(-maxPanY, Math.min(maxPanY, targetPanY));
            const rotation = direction.x * (segmentProgress - 0.5) * 4.2 * (Math.PI / 180);

            const cx = canvas.width / 2 + panX;
            const cy = canvas.height / 2 + panY;

            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(rotation);
            ctx.drawImage(media, -w / 2, -h / 2, w, h);
            ctx.restore();
          } else {
            ctx.fillStyle = "#000";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
        } else if (media instanceof HTMLVideoElement && media.readyState >= 2) {
          // Loop video if it ends before the B-roll segment
          if (media.ended || media.paused) {
            media.currentTime = 0;
            media.play().catch(() => {});
          }

          // Scale video to cover canvas (center crop)
          const scale = Math.max(canvas.width / media.videoWidth, canvas.height / media.videoHeight);
          const w = media.videoWidth * scale;
          const h = media.videoHeight * scale;
          const x = (canvas.width - w) / 2;
          const y = (canvas.height - h) / 2;
          ctx.drawImage(media, x, y, w, h);
        } else {
          // Fallback: black frame
          ctx.fillStyle = "#000";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        // Draw community name at the bottom
        this.drawAppName(ctx, canvas.width, canvas.height, communityName + " App");

        if (elapsed < totalDuration) {
          requestAnimationFrame(renderFrame);
        } else {
          // Stop recording
          mediaRecorder.stop();
          resolve();
        }
      };

      renderFrame();
    });

    // Wait for recording to finish
    await new Promise<void>((resolve) => {
      mediaRecorder.onstop = () => resolve();
    });

    // Cleanup
    audioContext.close();
    for (const audio of audioElements) {
      audio.pause();
      audio.src = "";
    }
    if (backgroundMusicElement) {
      backgroundMusicElement.pause();
      backgroundMusicElement.src = "";
    }
    for (const media of mediaElements) {
      if (media instanceof HTMLVideoElement) {
        media.pause();
        media.src = "";
      }
    }

    // Create blob URL
    const blob = new Blob(chunks, { type: "video/webm" });
    return URL.createObjectURL(blob);
  };

  generateStockFootageReel = async () => {
    const { audioFiles, text } = this.props;
    const BROLL_DURATION = 5; // seconds per B-roll clip

    if (!text) {
      alert("Please add some text content first.");
      return;
    }

    if (audioFiles.length === 0) {
      alert("No audio files found. Please generate audio first.");
      return;
    }

    this.setState({
      isGeneratingReel: true,
      reelProgress: "Calculating total duration...",
      progressPercent: 5,
      brollSelections: [],
      aiPromptInputs: [],
      renderedVideoUrl: null,
      hasConfirmedBrolls: false,
      backgroundMusicUrl: null,
      currentStep: "finding",
    });

    try {
      // Step 1: Calculate total audio duration to determine number of B-rolls needed
      let totalAudioDuration = 0;
      for (const file of audioFiles) {
        try {
          const duration = await this.getAudioDuration(file.url);
          totalAudioDuration += duration;
        } catch (e) {
          console.error("Error getting duration for:", file.name, e);
        }
      }

      const numBrolls = Math.ceil(totalAudioDuration / BROLL_DURATION);
      console.log(`Total duration: ${totalAudioDuration}s, need ${numBrolls} B-rolls (${BROLL_DURATION}s each)`);

      // Step 2: Split text into segments for each 5-second B-roll
      const allWords = text.split(/\s+/).filter((w: string) => w.length > 0);
      const wordsPerSecond = allWords.length / totalAudioDuration;
      const wordsPerBroll = Math.ceil(wordsPerSecond * BROLL_DURATION);

      // Create text segments for each B-roll
      const textSegments: string[] = [];
      for (let i = 0; i < numBrolls; i++) {
        const startWord = i * wordsPerBroll;
        const endWord = Math.min(startWord + wordsPerBroll, allWords.length);
        const segment = allWords.slice(startWord, endWord).join(" ");
        textSegments.push(segment);
      }

      console.log(`Created ${textSegments.length} text segments for B-roll matching`);

      this.setState({ reelProgress: "Preparing styled AI image prompts...", progressPercent: 8 });
      const { visualStyle, writingStyle } = await this.loadCommunityStylesForPrompts();
      const autoImagePrompts = await this.generateStyledImagePrompts(
        textSegments,
        visualStyle,
        writingStyle
      );

      // Step 3: Extract a keyword for EACH 5-second segment and find matching video options
      const brollSelections: BrollSelection[] = [];
      const aiPromptInputs: string[] = [];

      for (let i = 0; i < textSegments.length; i++) {
        const progressPercent = 10 + Math.floor((i / textSegments.length) * 80);
        this.setState({ reelProgress: `Finding B-roll ${i + 1}/${textSegments.length}...`, progressPercent });

        // Extract keyword for this specific segment
        const keyword = await this.extractKeywordFromParagraph(textSegments[i]);
        console.log(`Segment ${i + 1} keyword: "${keyword}" (from: "${textSegments[i].substring(0, 50)}...")`);

        // Search for videos matching this segment's keyword (up to 3 options)
        let options: BrollOption[] = await this.searchVideos(keyword);

        // Try multiple fallback searches to ensure we NEVER skip a segment
        // Skipping segments causes captions to get out of sync with audio
        if (options.length === 0) {
          options = await this.searchVideos("peaceful nature");
        }
        if (options.length === 0) {
          options = await this.searchVideos("abstract background");
        }
        if (options.length === 0) {
          options = await this.searchVideos("sky clouds");
        }
        if (options.length === 0) {
          options = await this.searchVideos("water");
        }

        // If still no videos found, we MUST still include this segment to maintain sync
        // Use the previous segment's video options as a last resort
        if (options.length === 0 && brollSelections.length > 0) {
          console.warn(`No videos found for segment ${i + 1}, reusing previous segment's videos`);
          options = [...brollSelections[brollSelections.length - 1].options];
        }

        // Only skip if we truly have no options AND no previous segments (extremely rare)
        if (options.length === 0) {
          console.error(`Failed to find any videos for segment ${i + 1}, this will cause sync issues`);
          continue;
        }

        brollSelections.push({
          keyword,
          textSegment: textSegments[i],
          options,
          selectedIndex: 0,
        });
        aiPromptInputs.push(
          autoImagePrompts[i] ||
            this.buildFallbackStyledPrompt(textSegments[i], visualStyle, writingStyle)
        );
      }

      // Verify we have the correct number of B-rolls to maintain audio/caption sync
      if (brollSelections.length !== numBrolls) {
        console.warn(
          `B-roll count mismatch: expected ${numBrolls} but got ${brollSelections.length}. ` +
          `This may cause caption sync issues.`
        );
      }

      this.setState((prev) => ({
        brollSelections,
        aiPromptInputs,
        nanoBananaTheme: prev.nanoBananaTheme || visualStyle || "",
        reelProgress: brollSelections.length
          ? "Review and select B-rolls for each segment"
          : "No videos found. Try different content.",
        progressPercent: 100,
        currentStep: brollSelections.length ? "selecting" : "idle",
      }));

      if (brollSelections.length === 0) {
        return;
      }
    } catch (error) {
      console.error("Error generating reel:", error);
      this.setState({ reelProgress: "Error generating reel. Please try again.", currentStep: "idle" });
    } finally {
      this.setState({ isGeneratingReel: false });
    }
  };

  // Download all audio files individually
  downloadAudio = () => {
    const { audioFiles } = this.props;
    if (audioFiles.length === 0) return;

    // Download each audio file
    audioFiles.forEach((file, index) => {
      setTimeout(() => {
        const a = document.createElement("a");
        a.href = file.url;
        a.download = file.name || `audio-${index + 1}.mp3`;
        a.target = "_blank";
        a.click();
      }, index * 300); // Stagger downloads to prevent browser blocking
    });
  };

  // Combine all audio files into one and download
  downloadCombinedAudio = async () => {
    const { audioFiles } = this.props;
    if (audioFiles.length === 0) return;

    // If only one file, just download it directly
    if (audioFiles.length === 1) {
      const a = document.createElement("a");
      a.href = audioFiles[0].url;
      a.download = audioFiles[0].name || "audio.mp3";
      a.target = "_blank";
      a.click();
      return;
    }

    this.setState({ isConvertingToMp4: true, reelProgress: "Loading audio processor...", progressPercent: 5 });

    try {
      // Load FFmpeg from CDN
      await this.loadScript("https://cdn.jsdelivr.net/npm/@ffmpeg/ffmpeg@0.11.6/dist/ffmpeg.min.js");

      const { createFFmpeg, fetchFile } = (window as any).FFmpeg;

      this.setState({ reelProgress: "Initializing audio processor...", progressPercent: 10 });

      const ffmpeg = createFFmpeg({
        log: true,
        corePath: "https://unpkg.com/@ffmpeg/core-st@0.11.1/dist/ffmpeg-core.js",
        mainName: "main",
      });

      if (!ffmpeg.isLoaded()) {
        this.setState({ reelProgress: "Loading audio encoder...", progressPercent: 15 });
        await ffmpeg.load();
      }

      // Fetch and write all audio files
      const inputFiles: string[] = [];
      for (let i = 0; i < audioFiles.length; i++) {
        const progress = 20 + Math.floor((i / audioFiles.length) * 30);
        this.setState({ reelProgress: `Fetching audio ${i + 1}/${audioFiles.length}...`, progressPercent: progress });

        const response = await fetch(audioFiles[i].url);
        const blob = await response.blob();
        const fileName = `input${i}.mp3`;
        ffmpeg.FS("writeFile", fileName, await fetchFile(blob));
        inputFiles.push(fileName);
      }

      // Create concat file list
      this.setState({ reelProgress: "Combining audio files...", progressPercent: 55 });
      const concatList = inputFiles.map(f => `file '${f}'`).join("\n");
      ffmpeg.FS("writeFile", "concat.txt", concatList);

      // Concatenate audio files
      this.setState({ reelProgress: "Processing combined audio...", progressPercent: 65 });
      await ffmpeg.run(
        "-f", "concat",
        "-safe", "0",
        "-i", "concat.txt",
        "-c:a", "libmp3lame",
        "-b:a", "192k",
        "-y",
        "combined.mp3"
      );

      this.setState({ reelProgress: "Preparing download...", progressPercent: 90 });

      // Read output file
      const mp3Data = ffmpeg.FS("readFile", "combined.mp3");
      const mp3Blob = new Blob([mp3Data.buffer], { type: "audio/mpeg" });
      const mp3Url = URL.createObjectURL(mp3Blob);

      // Cleanup
      inputFiles.forEach(f => ffmpeg.FS("unlink", f));
      ffmpeg.FS("unlink", "concat.txt");
      ffmpeg.FS("unlink", "combined.mp3");

      // Download
      const a = document.createElement("a");
      a.href = mp3Url;
      a.download = `combined-audio-${Date.now()}.mp3`;
      a.click();

      this.setState({ reelProgress: "Audio combined successfully!", progressPercent: 100 });

      // Clean up blob URL after download
      setTimeout(() => URL.revokeObjectURL(mp3Url), 1000);
    } catch (error) {
      console.error("Error combining audio:", error);
      this.setState({ reelProgress: "Error combining audio. Downloading separately..." });
      // Fallback to individual downloads
      this.downloadAudio();
    } finally {
      this.setState({ isConvertingToMp4: false });
    }
  };

  handleClear = () => {
    this.setState({
      renderedVideoUrl: null,
      cloudVideoUrl: null,
      brollSelections: [],
      reelProgress: "",
      progressPercent: 0,
      hasConfirmedBrolls: false,
      backgroundMusicUrl: null,
      searchInputs: [],
      aiPromptInputs: [],
      searchLoadingIndex: null,
      currentStep: "idle",
      expandedBrollIndex: null,
      videoSource: "pexels",
      isCreatingNewReel: false,
      freeFlowSegments: [],
      freeFlowCurrentStep: "composing",
    });
  };

  // ===== Free Flow Methods =====

  handleAddFreeFlowSegments = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newSegments: FreeFlowSegment[] = [];

    Array.from(files).forEach((file) => {
      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");
      if (!isVideo && !isImage) return;

      const mediaUrl = URL.createObjectURL(file);
      newSegments.push({
        id: `seg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: isVideo ? "video" : "image",
        mediaUrl,
        mediaThumbnail: isVideo ? "" : mediaUrl,
        caption: "",
        duration: isVideo ? 1.5 : 0.5,
        file,
      });

      // Generate thumbnail for videos
      if (isVideo) {
        const video = document.createElement("video");
        video.src = mediaUrl;
        video.muted = true;
        video.onloadeddata = () => {
          video.currentTime = 0.5;
        };
        video.onseeked = () => {
          const canvas = document.createElement("canvas");
          canvas.width = 160;
          canvas.height = 284;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            const scale = Math.max(canvas.width / video.videoWidth, canvas.height / video.videoHeight);
            const w = video.videoWidth * scale;
            const h = video.videoHeight * scale;
            ctx.drawImage(video, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
            const thumb = canvas.toDataURL("image/jpeg", 0.7);
            this.setState((prev) => ({
              freeFlowSegments: prev.freeFlowSegments.map((s) =>
                s.mediaUrl === mediaUrl ? { ...s, mediaThumbnail: thumb } : s
              ),
            }));
          }
        };
      }
    });

    this.setState((prev) => ({
      freeFlowSegments: [...prev.freeFlowSegments, ...newSegments],
    }));

    // Reset file input
    e.target.value = "";
  };

  handleRemoveFreeFlowSegment = (id: string) => {
    this.setState((prev) => {
      const seg = prev.freeFlowSegments.find((s) => s.id === id);
      if (seg) URL.revokeObjectURL(seg.mediaUrl);
      return { freeFlowSegments: prev.freeFlowSegments.filter((s) => s.id !== id) };
    });
  };

  handleMoveFreeFlowSegment = (index: number, direction: "up" | "down") => {
    this.setState((prev) => {
      const segments = [...prev.freeFlowSegments];
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= segments.length) return { freeFlowSegments: segments };
      [segments[index], segments[targetIndex]] = [segments[targetIndex], segments[index]];
      return { freeFlowSegments: segments };
    });
  };

  handleUpdateFreeFlowSegment = (id: string, updates: Partial<FreeFlowSegment>) => {
    this.setState((prev) => ({
      freeFlowSegments: prev.freeFlowSegments.map((s) =>
        s.id === id ? { ...s, ...updates } : s
      ),
    }));
  };

  // --- Drag-reorder segments ---
  handleSegmentDragStart = (segId: string) => (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", segId); } catch { /* noop */ }
    this.setState({ draggingSegmentId: segId });
  };

  handleSegmentDragOver = (segId: string) => (e: React.DragEvent<HTMLDivElement>) => {
    if (!this.state.draggingSegmentId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (this.state.dropTargetSegmentId !== segId) {
      this.setState({ dropTargetSegmentId: segId });
    }
  };

  handleSegmentDrop = (targetSegId: string) => (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const sourceSegId = this.state.draggingSegmentId;
    this.setState({ draggingSegmentId: null, dropTargetSegmentId: null });
    if (!sourceSegId || sourceSegId === targetSegId) return;

    this.setState((prev) => {
      const segments = [...prev.freeFlowSegments];
      const sourceIdx = segments.findIndex((s) => s.id === sourceSegId);
      const targetIdx = segments.findIndex((s) => s.id === targetSegId);
      if (sourceIdx < 0 || targetIdx < 0) return { freeFlowSegments: prev.freeFlowSegments };
      const [moved] = segments.splice(sourceIdx, 1);
      segments.splice(targetIdx, 0, moved);
      return { freeFlowSegments: segments };
    });
  };

  handleSegmentDragEnd = () => {
    this.setState({ draggingSegmentId: null, dropTargetSegmentId: null });
  };

  // --- Variations ---
  handleAddTextVariation = (segId: string) => {
    this.setState((prev) => ({
      freeFlowSegments: prev.freeFlowSegments.map((s) => {
        if (s.id !== segId) return s;
        return { ...s, captionVariations: [...(s.captionVariations || []), ""] };
      }),
    }));
  };

  handleUpdateTextVariation = (segId: string, index: number, text: string) => {
    this.setState((prev) => ({
      freeFlowSegments: prev.freeFlowSegments.map((s) => {
        if (s.id !== segId) return s;
        const next = [...(s.captionVariations || [])];
        next[index] = text;
        return { ...s, captionVariations: next };
      }),
    }));
  };

  handleRemoveTextVariation = (segId: string, index: number) => {
    this.setState((prev) => ({
      freeFlowSegments: prev.freeFlowSegments.map((s) => {
        if (s.id !== segId) return s;
        const next = (s.captionVariations || []).filter((_, i) => i !== index);
        return { ...s, captionVariations: next.length ? next : undefined };
      }),
    }));
  };

  handleAddClipVariation = (segId: string, item: VideoLibraryItem) => {
    const variation: FreeFlowClipVariation = {
      libraryItemId: item.id,
      type: item.type,
      mediaUrl: item.mediaUrl,
      mediaThumbnail: item.mediaThumbnail || item.mediaUrl,
      name: item.name,
    };
    this.setState((prev) => ({
      freeFlowSegments: prev.freeFlowSegments.map((s) => {
        if (s.id !== segId) return s;
        return { ...s, clipVariations: [...(s.clipVariations || []), variation] };
      }),
      clipPickerForSegId: null,
    }));
  };

  handleRemoveClipVariation = (segId: string, index: number) => {
    this.setState((prev) => ({
      freeFlowSegments: prev.freeFlowSegments.map((s) => {
        if (s.id !== segId) return s;
        const next = (s.clipVariations || []).filter((_, i) => i !== index);
        return { ...s, clipVariations: next.length ? next : undefined };
      }),
    }));
  };

  // --- Batch render ---
  getFolderPoolForSegment = (seg: FreeFlowSegment): FreeFlowClipVariation[] => {
    if (!seg.clipFolderPool) return [];
    const folder = seg.clipFolderPool;
    return this.state.videoLibrary
      .filter((i) => (i.folder || "") === folder)
      .map((i) => ({
        libraryItemId: i.id,
        type: i.type,
        mediaUrl: i.mediaUrl,
        mediaThumbnail: i.mediaThumbnail || i.mediaUrl,
        name: i.name,
      }));
  };

  computeBatchTotals = (segments: FreeFlowSegment[]) => {
    let fullMatrix = 1;
    for (const seg of segments) {
      const textOpts = 1 + (seg.captionVariations?.filter((t) => t.trim() !== "").length || 0);
      const folderPoolSize = this.getFolderPoolForSegment(seg).length;
      const clipOpts = 1 + (seg.clipVariations?.length || 0) + folderPoolSize;
      fullMatrix *= textOpts * clipOpts;
    }
    return { fullMatrix };
  };

  buildVariationSegments = (segments: FreeFlowSegment[]): FreeFlowSegment[] =>
    segments.map((seg) => {
      const textPool = [seg.caption, ...(seg.captionVariations || [])]
        .filter((t) => t.trim() !== "");
      const safeTextPool = textPool.length ? textPool : [seg.caption];
      const pickedCaption = safeTextPool[Math.floor(Math.random() * safeTextPool.length)];

      // Clip pool = primary clip + explicit alternates + every item in the bound folder
      const folderPool = this.getFolderPoolForSegment(seg);
      const clipPool: FreeFlowClipVariation[] = [
        {
          libraryItemId: seg.libraryItemId,
          type: seg.type,
          mediaUrl: seg.mediaUrl,
          mediaThumbnail: seg.mediaThumbnail,
        },
        ...(seg.clipVariations || []),
        ...folderPool,
      ];
      // Dedupe by libraryItemId so a clip that's both the primary AND in its folder
      // doesn't appear twice in the pool (which would bias sampling).
      const dedupedPool: FreeFlowClipVariation[] = [];
      const seen = new Set<string>();
      for (const c of clipPool) {
        const key = c.libraryItemId || c.mediaUrl;
        if (seen.has(key)) continue;
        seen.add(key);
        dedupedPool.push(c);
      }
      const pickedClip = dedupedPool[Math.floor(Math.random() * dedupedPool.length)];

      return {
        ...seg,
        caption: pickedCaption,
        type: pickedClip.type,
        mediaUrl: pickedClip.mediaUrl,
        mediaThumbnail: pickedClip.mediaThumbnail,
        libraryItemId: pickedClip.libraryItemId,
        captionVariations: undefined,
        clipVariations: undefined,
        clipFolderPool: undefined,
      };
    });

  uploadBatchBlob = async (blob: Blob, communityId: string, stepId: string, index: number, ext: "mp4" | "webm" = "mp4"): Promise<string> => {
    const path = `${DB_PATH.VIDEO}/${communityId}/${stepId}/batch/${Date.now()}-${index}.${ext}`;
    return (
      await uploadMedia({
        body: blob,
        kind: "video",
        contentType: blob.type || (ext === "webm" ? "video/webm" : "video/mp4"),
        fileName: `batch.${ext}`,
        firebasePath: path,
      })
    ).url;
  };

  handleRunBatchRender = async () => {
    const { freeFlowSegments, batchCount } = this.state;
    const { communityId, stepId } = this.props;
    if (freeFlowSegments.length === 0 || !communityId || !stepId) return;

    const total = Math.max(1, Math.min(64, Math.floor(batchCount) || 1));
    this.setState({
      isBatchRendering: true,
      batchCancelRequested: false,
      batchProgress: { done: 0, total },
    });

    try {
      const communityName = await this.loadCommunityName();
      for (let i = 0; i < total; i++) {
        if (this.state.batchCancelRequested) break;
        const variant = this.buildVariationSegments(freeFlowSegments);

        // Render (0-70% of this iteration's portion)
        const webmBlob = await this.renderSegmentsToBlob(variant, {
          communityName,
          onProgress: ({ stage, percent }) => {
            const overall = ((i + (percent * 0.7) / 100) / total) * 100;
            this.setState({
              reelProgress: `Batch ${i + 1}/${total} — ${stage}`,
              progressPercent: Math.floor(overall),
            });
          },
        });

        // Convert to MP4 (70-95% of this iteration's portion)
        let outputBlob = webmBlob;
        let outputExt: "mp4" | "webm" = "webm";
        try {
          const { blob: mp4Blob } = await this.convertToMp4(webmBlob, {
            silent: true,
            onProgress: ({ percent }) => {
              const overall = ((i + 0.7 + (percent * 0.25) / 100) / total) * 100;
              this.setState({
                reelProgress: `Batch ${i + 1}/${total} — Encoding MP4 ${Math.floor(percent)}%`,
                progressPercent: Math.floor(overall),
              });
            },
          });
          outputBlob = mp4Blob;
          outputExt = "mp4";
        } catch (error) {
          console.warn(`Batch ${i + 1}: MP4 conversion failed, uploading WebM`, error);
        }

        // Upload (95-100%)
        const url = await this.uploadBatchBlob(outputBlob, communityId, stepId, i + 1, outputExt);
        this.setState((prev) => ({
          batchResults: [
            ...prev.batchResults,
            { url, createdAt: new Date().toISOString(), sizeBytes: outputBlob.size, ext: outputExt },
          ] as State["batchResults"],
          batchProgress: { done: i + 1, total },
        }));
      }
    } catch (error) {
      console.error("Batch render failed:", error);
      const msg = error instanceof Error ? error.message : "Batch render failed.";
      alert(msg);
    } finally {
      this.setState({ isBatchRendering: false, batchCancelRequested: false });
    }
  };

  handleCancelBatchRender = () => {
    this.setState({ batchCancelRequested: true });
  };

  handleClearBatchResults = () => {
    this.setState({ batchResults: [] });
  };

  // --- Combinations Builder ---
  handleAddCombinationPool = () => {
    const folderSet = new Set<string>();
    for (const i of this.state.videoLibrary) {
      if (i.folder) folderSet.add(i.folder);
    }
    const firstFolder = Array.from(folderSet).sort()[0] || "";
    this.setState((prev) => ({
      combinationPools: [
        ...prev.combinationPools,
        {
          id: `pool-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          folder: firstFolder,
          durationSec: prev.combinationPools.length === 0 ? 3 : 5,
          caption: "",
        },
      ],
    }));
  };

  handleUpdateCombinationPool = (
    poolId: string,
    updates: Partial<State["combinationPools"][number]>
  ) => {
    this.setState((prev) => ({
      combinationPools: prev.combinationPools.map((p) =>
        p.id === poolId ? { ...p, ...updates } : p
      ),
    }));
  };

  handleRemoveCombinationPool = (poolId: string) => {
    this.setState((prev) => ({
      combinationPools: prev.combinationPools.filter((p) => p.id !== poolId),
    }));
  };

  handleMoveCombinationPool = (poolId: string, direction: "up" | "down") => {
    this.setState((prev) => {
      const pools = [...prev.combinationPools];
      const idx = pools.findIndex((p) => p.id === poolId);
      if (idx < 0) return { combinationPools: pools };
      const target = direction === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= pools.length) return { combinationPools: pools };
      [pools[idx], pools[target]] = [pools[target], pools[idx]];
      return { combinationPools: pools };
    });
  };

  computeCombinationTotals = (): {
    pools: Array<{ folder: string; items: VideoLibraryItem[] }>;
    total: number;
    missingFolder: boolean;
    emptyFolder: boolean;
  } => {
    const pools = this.state.combinationPools.map((p) => ({
      folder: p.folder,
      items: this.state.videoLibrary.filter((i) => (i.folder || "") === p.folder),
    }));
    const missingFolder = this.state.combinationPools.some((p) => !p.folder);
    const emptyFolder = pools.some((p) => p.items.length === 0);
    const total = pools.length === 0
      ? 0
      : pools.reduce((acc, p) => acc * Math.max(1, p.items.length), 1);
    return { pools, total, missingFolder, emptyFolder };
  };

  buildSegmentsForCombination = (
    pools: Array<{ items: VideoLibraryItem[] }>,
    indices: number[],
    poolMeta: State["combinationPools"]
  ): FreeFlowSegment[] =>
    pools.map((p, i) => {
      const item = p.items[indices[i]];
      const meta = poolMeta[i];
      return {
        id: `combo-seg-${i}-${Math.random().toString(36).slice(2, 6)}`,
        type: item.type,
        mediaUrl: item.mediaUrl,
        mediaThumbnail: item.mediaThumbnail || item.mediaUrl,
        caption: meta.caption,
        duration: Math.max(0.5, meta.durationSec || 3),
        libraryItemId: item.id,
      };
    });

  handleRunCombinationBatch = async (mode: "all" | "sample") => {
    const { combinationPools } = this.state;
    const { communityId, stepId } = this.props;
    if (!communityId || !stepId || combinationPools.length === 0) return;

    const { pools, total, missingFolder, emptyFolder } = this.computeCombinationTotals();
    if (missingFolder) {
      alert("Pick a folder for every pool first.");
      return;
    }
    if (emptyFolder) {
      alert("One of the folders has no clips — drop something into it first.");
      return;
    }
    if (total === 0) return;

    // Enumerate which combos to render
    const sampleCap = 16;
    let plan: number[][];
    if (mode === "all") {
      plan = [];
      const cap = Math.min(total, 64);
      // Generate Cartesian product, capped
      const stack: number[] = [];
      const recurse = (depth: number) => {
        if (plan.length >= cap) return;
        if (depth === pools.length) {
          plan.push([...stack]);
          return;
        }
        for (let i = 0; i < pools[depth].items.length; i++) {
          if (plan.length >= cap) return;
          stack.push(i);
          recurse(depth + 1);
          stack.pop();
        }
      };
      recurse(0);
    } else {
      const count = Math.min(sampleCap, total);
      plan = [];
      const seen = new Set<string>();
      while (plan.length < count) {
        const combo = pools.map((p) => Math.floor(Math.random() * p.items.length));
        const key = combo.join("-");
        if (seen.has(key)) continue;
        seen.add(key);
        plan.push(combo);
      }
    }

    this.setState({
      isBatchRendering: true,
      batchCancelRequested: false,
      batchProgress: { done: 0, total: plan.length },
    });

    try {
      const communityName = await this.loadCommunityName();
      for (let i = 0; i < plan.length; i++) {
        if (this.state.batchCancelRequested) break;
        const segments = this.buildSegmentsForCombination(pools, plan[i], combinationPools);
        const webmBlob = await this.renderSegmentsToBlob(segments, {
          communityName,
          onProgress: ({ stage, percent }) => {
            const overall = ((i + (percent * 0.7) / 100) / plan.length) * 100;
            this.setState({
              reelProgress: `Combo ${i + 1}/${plan.length} — ${stage}`,
              progressPercent: Math.floor(overall),
            });
          },
        });
        let outputBlob: Blob = webmBlob;
        let outputExt: "mp4" | "webm" = "webm";
        try {
          const { blob: mp4Blob } = await this.convertToMp4(webmBlob, {
            silent: true,
            onProgress: ({ percent }) => {
              const overall = ((i + 0.7 + (percent * 0.25) / 100) / plan.length) * 100;
              this.setState({
                reelProgress: `Combo ${i + 1}/${plan.length} — Encoding MP4 ${Math.floor(percent)}%`,
                progressPercent: Math.floor(overall),
              });
            },
          });
          outputBlob = mp4Blob;
          outputExt = "mp4";
        } catch (error) {
          console.warn(`Combo ${i + 1}: MP4 conversion failed, uploading WebM`, error);
        }

        const url = await this.uploadBatchBlob(outputBlob, communityId, stepId, i + 1, outputExt);
        this.setState((prev) => ({
          batchResults: [
            ...prev.batchResults,
            { url, createdAt: new Date().toISOString(), sizeBytes: outputBlob.size, ext: outputExt },
          ],
          batchProgress: { done: i + 1, total: plan.length },
        }));
      }
    } catch (error) {
      console.error("Combination batch failed:", error);
      const msg = error instanceof Error ? error.message : "Combination batch failed.";
      alert(msg);
    } finally {
      this.setState({ isBatchRendering: false, batchCancelRequested: false });
    }
  };

  handleAddSegmentFromLibrary = (item: VideoLibraryItem) => {
    const segment: FreeFlowSegment = {
      id: `seg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: item.type,
      mediaUrl: item.mediaUrl,
      mediaThumbnail: item.mediaThumbnail || item.mediaUrl,
      caption: "",
      duration: item.type === "video"
        ? Math.min(Math.max(item.durationSec ?? 1.5, 0.5), 10)
        : 0.5,
      libraryItemId: item.id,
    };
    this.setState((prev) => ({
      freeFlowSegments: [...prev.freeFlowSegments, segment],
    }));
  };

  handleUploadToLibrary = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    const { communityId } = this.props;
    if (!files || !communityId) {
      e.target.value = "";
      return;
    }

    const fileArray = Array.from(files);
    e.target.value = "";

    const queue = fileArray.map((file) => ({
      file,
      uploadId: `up-${Date.now()}-${Math.random().toString(36).slice(2, 7)}-${file.name}`,
    }));

    this.setState((prev) => ({
      libraryUploads: [
        ...prev.libraryUploads,
        ...queue.map((q) => ({ id: q.uploadId, name: q.file.name, percent: 0 })),
      ],
    }));

    const defaultFolder = this.state.selectedLibraryFolder || undefined;

    const runOne = async (file: File, uploadId: string) => {
      try {
        await uploadVideoLibraryItem(communityId, file, {
          folder: defaultFolder,
          onProgress: (percent) => {
            this.setState((prev) => ({
              libraryUploads: prev.libraryUploads.map((u) =>
                u.id === uploadId ? { ...u, percent } : u
              ),
            }));
          },
        });
      } catch (error) {
        console.error("Failed to upload to library:", file.name, error);
      } finally {
        this.setState((prev) => ({
          libraryUploads: prev.libraryUploads.filter((u) => u.id !== uploadId),
        }));
      }
    };

    const concurrency = 3;
    let cursor = 0;
    const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
      while (cursor < queue.length) {
        const idx = cursor++;
        const next = queue[idx];
        if (!next) return;
        await runOne(next.file, next.uploadId);
      }
    });
    await Promise.all(workers);
  };

  handleRemoveLibraryItem = async (item: VideoLibraryItem) => {
    const { communityId } = this.props;
    if (!communityId) return;
    if (typeof window !== "undefined" && !window.confirm(`Delete "${item.name}" from the library?`)) {
      return;
    }
    try {
      await removeVideoLibraryItem(communityId, item);
      this.setState((prev) => ({
        freeFlowSegments: prev.freeFlowSegments.map((s) =>
          s.libraryItemId === item.id ? { ...s, libraryItemId: undefined } : s
        ),
      }));
    } catch (error) {
      console.error("Failed to remove library item:", error);
    }
  };

  handleOpenFolderPicker = (item: VideoLibraryItem) => {
    this.setState({ folderPickerForItemId: item.id });
  };

  handleCommitFolderForItem = async (item: VideoLibraryItem, nextFolder: string | null) => {
    const { communityId } = this.props;
    this.setState({ folderPickerForItemId: null });
    if (!communityId) return;
    try {
      await setVideoLibraryItemFolder(communityId, item, nextFolder);
    } catch (error) {
      console.error("Failed to assign folder:", error);
    }
  };

  handleCommitNewFolderForItem = async (item: VideoLibraryItem) => {
    if (typeof window === "undefined") return;
    const next = window.prompt(`New folder name for "${item.name}"`, "");
    if (next === null || !next.trim()) {
      this.setState({ folderPickerForItemId: null });
      return;
    }
    await this.handleCommitFolderForItem(item, next.trim());
  };

  toggleLibraryItemSelected = (itemId: string) => {
    this.setState((prev) => {
      const next = { ...prev.selectedLibraryItemIds };
      if (next[itemId]) delete next[itemId];
      else next[itemId] = true;
      return { selectedLibraryItemIds: next };
    });
  };

  clearLibrarySelection = () => {
    this.setState({ selectedLibraryItemIds: {} });
  };

  selectAllFilteredLibraryItems = (filtered: VideoLibraryItem[]) => {
    const next: Record<string, true> = { ...this.state.selectedLibraryItemIds };
    for (const item of filtered) next[item.id] = true;
    this.setState({ selectedLibraryItemIds: next });
  };

  handleBulkDelete = async () => {
    const { communityId } = this.props;
    if (!communityId) return;
    const ids = Object.keys(this.state.selectedLibraryItemIds);
    if (ids.length === 0) return;
    if (typeof window !== "undefined" && !window.confirm(`Delete ${ids.length} item${ids.length !== 1 ? "s" : ""} from the library?`)) {
      return;
    }
    const targets = this.state.videoLibrary.filter((i) => this.state.selectedLibraryItemIds[i.id]);
    for (const item of targets) {
      try {
        await removeVideoLibraryItem(communityId, item);
      } catch (error) {
        console.warn("Bulk delete failed for", item.name, error);
      }
    }
    this.setState({ selectedLibraryItemIds: {} });
  };

  handleBulkAssignFolder = async (folder: string | null) => {
    const { communityId } = this.props;
    if (!communityId) return;
    const targets = this.state.videoLibrary.filter((i) => this.state.selectedLibraryItemIds[i.id]);
    this.setState({ bulkFolderPickerOpen: false });
    for (const item of targets) {
      try {
        await setVideoLibraryItemFolder(communityId, item, folder);
      } catch (error) {
        console.warn("Bulk move failed for", item.name, error);
      }
    }
  };

  handleBulkAssignNewFolder = async () => {
    if (typeof window === "undefined") return;
    const next = window.prompt("New folder name for selected items", "");
    if (next === null || !next.trim()) {
      this.setState({ bulkFolderPickerOpen: false });
      return;
    }
    await this.handleBulkAssignFolder(next.trim());
  };

  handleLibraryDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDraggingOverLibrary: false });
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    // Reuse the same upload pipeline as the button by simulating a change event payload
    this.handleUploadToLibrary({
      target: {
        files,
        value: "",
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  handleBackfillMissingThumbnails = async (mode: "missing" | "all" = "missing") => {
    const { communityId } = this.props;
    if (!communityId) return;

    const targets = this.state.videoLibrary.filter((item) =>
      item.type === "video" &&
      (mode === "all" || !item.mediaThumbnail || !item.mediaThumbnail.startsWith("data:"))
    );
    if (targets.length === 0) return;

    this.setState({
      isBackfillingThumbnails: true,
      backfillProgress: { done: 0, total: targets.length },
    });

    let done = 0;
    for (const item of targets) {
      try {
        await backfillVideoLibraryThumbnail(communityId, item);
      } catch (error) {
        console.warn("Backfill failed for", item.name, error);
      } finally {
        done += 1;
        this.setState({ backfillProgress: { done, total: targets.length } });
      }
    }

    this.setState({ isBackfillingThumbnails: false });
  };

  renderSegmentsToBlob = async (
    segments: FreeFlowSegment[],
    opts: {
      communityName: string;
      onProgress?: (info: { stage: string; percent: number }) => void;
    }
  ): Promise<Blob> => {
    const { communityName, onProgress } = opts;
    const report = (stage: string, percent: number) => onProgress?.({ stage, percent });

    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1920;
    const ctx = canvas.getContext("2d")!;

    const totalDuration = segments.reduce((sum, s) => sum + s.duration, 0) * 1000; // ms

    // Preload all media
    report("Loading media...", 5);
    const mediaElements: (HTMLImageElement | HTMLVideoElement)[] = [];
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      report(`Loading media ${i + 1}/${segments.length}...`, 5 + Math.floor((i / segments.length) * 20));
      const segmentLabel = `Segment #${i + 1}`;
      if (seg.type === "image") {
        const img = await this.loadImageWithRetries(seg.mediaUrl, segmentLabel, 3);
        mediaElements.push(img);
      } else {
        const video = await this.loadVideoWithRetries(seg.mediaUrl, segmentLabel, 3);
        mediaElements.push(video);
      }
    }

    const canvasStream = canvas.captureStream(30);
    const mediaRecorder = new MediaRecorder(canvasStream, {
      mimeType: "video/webm;codecs=vp9",
      videoBitsPerSecond: 5000000,
    });

    const chunks: Blob[] = [];
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    mediaRecorder.start(100);

    const segmentTimings: { start: number; end: number }[] = [];
    let accum = 0;
    for (const seg of segments) {
      segmentTimings.push({ start: accum, end: accum + seg.duration * 1000 });
      accum += seg.duration * 1000;
    }

    const firstMedia = mediaElements[0];
    if (firstMedia instanceof HTMLVideoElement) {
      firstMedia.currentTime = 0;
      firstMedia.play().catch(() => {});
    }

    report("Rendering video...", 30);
    const startTime = performance.now();
    let currentSegIndex = 0;
    let lastSegIndex = -1;

    await new Promise<void>((resolve) => {
      const renderFrame = () => {
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / totalDuration, 1);
        report(`Rendering video... ${Math.floor(progress * 100)}%`, 30 + Math.floor(progress * 60));

        let segIdx = currentSegIndex;
        for (let i = 0; i < segmentTimings.length; i++) {
          if (elapsed >= segmentTimings[i].start && elapsed < segmentTimings[i].end) {
            segIdx = i;
            break;
          }
        }
        if (elapsed >= segmentTimings[segmentTimings.length - 1]?.end) {
          segIdx = segmentTimings.length - 1;
        }

        if (segIdx !== lastSegIndex) {
          lastSegIndex = segIdx;
          if (currentSegIndex !== segIdx && mediaElements[currentSegIndex] instanceof HTMLVideoElement) {
            (mediaElements[currentSegIndex] as HTMLVideoElement).pause();
          }
          currentSegIndex = segIdx;
          const media = mediaElements[currentSegIndex];
          if (media instanceof HTMLVideoElement) {
            media.currentTime = 0;
            media.play().catch(() => {});
          }
        }

        const media = mediaElements[currentSegIndex];
        if (media) {
          let srcWidth: number, srcHeight: number;
          if (media instanceof HTMLVideoElement) {
            srcWidth = media.videoWidth || canvas.width;
            srcHeight = media.videoHeight || canvas.height;
            if (media.ended || media.paused) {
              media.currentTime = 0;
              media.play().catch(() => {});
            }
          } else {
            srcWidth = media.naturalWidth || canvas.width;
            srcHeight = media.naturalHeight || canvas.height;
          }

          let extraScale = 1;
          let panX = 0;
          let panY = 0;
          let rotation = 0;
          if (segmentTimings[currentSegIndex]) {
            const segStart = segmentTimings[currentSegIndex].start;
            const segEnd = segmentTimings[currentSegIndex].end;
            const segProgress = Math.min((elapsed - segStart) / (segEnd - segStart), 1);
            const ease = segProgress < 0.5
              ? 2 * segProgress * segProgress
              : 1 - Math.pow(-2 * segProgress + 2, 2) / 2;

            const animType = currentSegIndex % 8;
            const pan = 50;
            switch (animType) {
              case 0:
                extraScale = 1 + ease * 0.18;
                panX = -ease * pan * 0.6;
                panY = -ease * pan;
                break;
              case 1:
                extraScale = 1.2 - ease * 0.12;
                panX = ease * pan * 0.4;
                panY = ease * pan * 0.3;
                break;
              case 2:
                extraScale = 1.1;
                panX = -pan + ease * pan * 2;
                break;
              case 3:
                extraScale = 1 + ease * 0.15;
                rotation = ease * 1.5 * (Math.PI / 180);
                break;
              case 4:
                extraScale = 1 + ease * 0.2;
                panX = ease * pan * 0.7;
                panY = ease * pan * 0.8;
                break;
              case 5:
                extraScale = 1.18 - ease * 0.1;
                rotation = -ease * 1.2 * (Math.PI / 180);
                panY = -ease * pan * 0.3;
                break;
              case 6:
                extraScale = 1.05 + ease * 0.08;
                panX = pan - ease * pan * 2;
                panY = -ease * pan * 0.2;
                break;
              case 7:
                extraScale = 1.12;
                panX = -pan * 0.8 + ease * pan * 1.6;
                panY = pan * 0.6 - ease * pan * 1.2;
                break;
            }
          }

          const scale = Math.max(canvas.width / srcWidth, canvas.height / srcHeight) * extraScale;
          const w = srcWidth * scale;
          const h = srcHeight * scale;
          const cx = canvas.width / 2 + panX;
          const cy = canvas.height / 2 + panY;

          ctx.save();
          if (rotation !== 0) {
            ctx.translate(cx, cy);
            ctx.rotate(rotation);
            ctx.drawImage(media, -w / 2, -h / 2, w, h);
          } else {
            ctx.drawImage(media, cx - w / 2, cy - h / 2, w, h);
          }
          ctx.restore();
        } else {
          ctx.fillStyle = "#000";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        const currentSeg = segments[currentSegIndex];
        if (currentSeg?.caption?.trim()) {
          const segTiming = segmentTimings[currentSegIndex];
          const segElapsedSec = segTiming ? (elapsed - segTiming.start) / 1000 : 0;
          const captionStart = currentSeg.captionStartSec ?? 0;
          const captionEnd = currentSeg.captionEndSec ?? currentSeg.duration;
          if (segElapsedSec >= captionStart && segElapsedSec <= captionEnd) {
            ctx.fillStyle = "rgba(0, 0, 0, 0.12)";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            this.drawFreeFlowCaption(ctx, currentSeg.caption, canvas.width, canvas.height);
          }
        }

        if (elapsed < totalDuration) {
          requestAnimationFrame(renderFrame);
        } else {
          mediaRecorder.stop();
          resolve();
        }
      };
      renderFrame();
    });

    await new Promise<void>((resolve) => {
      mediaRecorder.onstop = () => resolve();
    });

    for (const media of mediaElements) {
      if (media instanceof HTMLVideoElement) {
        media.pause();
        media.src = "";
      }
    }

    return new Blob(chunks, { type: "video/webm" });
  };

  renderFreeFlowReel = async () => {
    const { freeFlowSegments } = this.state;
    if (freeFlowSegments.length === 0) return;

    this.setState({
      isRenderingReel: true,
      freeFlowCurrentStep: "rendering",
      currentStep: "rendering",
      reelProgress: "Preparing reel...",
      progressPercent: 0,
    });

    try {
      const communityName = await this.loadCommunityName();
      const blob = await this.renderSegmentsToBlob(freeFlowSegments, {
        communityName,
        onProgress: ({ stage, percent }) => {
          this.setState({ reelProgress: stage, progressPercent: percent });
        },
      });

      const renderedVideoUrl = URL.createObjectURL(blob);
      this.setState({
        renderedVideoUrl,
        reelProgress: "Reel generated successfully!",
        progressPercent: 100,
        currentStep: "complete",
        freeFlowCurrentStep: "complete",
      });
    } catch (error) {
      console.error("Error rendering free flow reel:", error);
      this.setState({
        reelProgress: "Error rendering reel. Please try again.",
        currentStep: "idle",
        freeFlowCurrentStep: "composing",
      });
      const msg = error instanceof Error ? error.message : "Error rendering reel. Please try again.";
      alert(msg);
    } finally {
      this.setState({ isRenderingReel: false });
    }
  };

  drawFreeFlowCaption = (
    ctx: CanvasRenderingContext2D,
    caption: string,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const trimmed = caption.trim();
    if (!trimmed) return;

    // TikTok-native "Classic" caption: clean white text, mixed-case,
    // system sans-serif, lower-third placement, subtle shadow for legibility.
    const fontSize = 64;
    const fontWeight = 700;
    const fontStack = `${fontWeight} ${fontSize}px "TikTokSans", "Helvetica Neue", "Inter", "SF Pro Text", Arial, sans-serif`;
    const maxLineWidth = canvasWidth * 0.82;
    const lineHeight = fontSize * 1.18;
    const centerY = canvasHeight * 0.68;

    ctx.font = fontStack;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Word-wrap (preserve casing — TikTok keeps what you typed)
    const words = trimmed.split(/\s+/);
    const lines: string[] = [];
    let currentLine = "";
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width > maxLineWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);

    const totalHeight = lines.length * lineHeight;
    const startY = centerY - totalHeight / 2 + lineHeight / 2;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const y = startY + i * lineHeight;

      ctx.save();
      // Soft drop shadow — keeps text readable over busy frames without a box
      ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
      ctx.shadowBlur = 6;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 2;
      ctx.fillStyle = "#FFFFFF";
      ctx.fillText(line, canvasWidth / 2, y);
      ctx.restore();
    }
  };

  renderVideoLibraryModal = () => {
    const {
      isLibraryOpen,
      videoLibrary,
      libraryUploads,
      playingLibraryItemId,
      isBackfillingThumbnails,
      backfillProgress,
      selectedLibraryFolder,
      selectedLibraryItemIds,
      isDraggingOverLibrary,
      librarySearch,
      librarySortBy,
    } = this.state;
    if (!isLibraryOpen) return null;
    const selectedCount = Object.keys(selectedLibraryItemIds).length;

    const missingThumbCount = videoLibrary.filter(
      (i) => i.type === "video" && (!i.mediaThumbnail || !i.mediaThumbnail.startsWith("data:"))
    ).length;

    // Folder grouping
    const folderCounts = new Map<string, number>();
    let unsortedCount = 0;
    for (const item of videoLibrary) {
      const f = (item.folder || "").trim();
      if (!f) unsortedCount += 1;
      else folderCounts.set(f, (folderCounts.get(f) || 0) + 1);
    }
    const folderNames = Array.from(folderCounts.keys()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );

    const searchTerm = librarySearch.trim().toLowerCase();
    const filteredItems = videoLibrary.filter((item) => {
      if (selectedLibraryFolder === null) {/* All */}
      else if (selectedLibraryFolder === "") { if (item.folder) return false; }
      else if ((item.folder || "") !== selectedLibraryFolder) return false;
      if (searchTerm && !(item.name || "").toLowerCase().includes(searchTerm)) return false;
      return true;
    });

    // Count how many times each library item is used in the current reel
    const segmentUsageByLibraryId = new Map<string, number>();
    for (const seg of this.state.freeFlowSegments) {
      if (seg.libraryItemId) {
        segmentUsageByLibraryId.set(
          seg.libraryItemId,
          (segmentUsageByLibraryId.get(seg.libraryItemId) || 0) + 1
        );
      }
    }
    const sortedItems = [...filteredItems].sort((a, b) => {
      switch (librarySortBy) {
        case "oldest":
          return (a.createdAt || "").localeCompare(b.createdAt || "");
        case "name":
          return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
        case "usage":
          return (segmentUsageByLibraryId.get(b.id) || 0) - (segmentUsageByLibraryId.get(a.id) || 0);
        case "newest":
        default:
          return (b.createdAt || "").localeCompare(a.createdAt || "");
      }
    });

    const folderHeading =
      selectedLibraryFolder === null
        ? "All items"
        : selectedLibraryFolder === ""
          ? "Unsorted"
          : selectedLibraryFolder;

    const folderItemStyle = (active: boolean): React.CSSProperties => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      textAlign: "left",
      padding: "8px 12px",
      borderRadius: 8,
      background: active ? "var(--color-accent-subtle)" : "transparent",
      border: "none",
      color: active ? "var(--color-accent-active)" : "#334155",
      fontWeight: active ? 700 : 500,
      fontSize: 13,
      cursor: "pointer",
      marginBottom: 2,
    });

    const overlay = (
      <div
        onClick={() => this.setState({ isLibraryOpen: false, playingLibraryItemId: null })}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(15, 23, 42, 0.78)",
          zIndex: 9999,
          display: "flex",
          alignItems: "stretch",
          justifyContent: "stretch",
          padding: 0,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff",
            borderRadius: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "row",
            boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)",
          }}
        >
          {/* Sidebar: folders */}
          <aside style={{
            width: 240,
            flexShrink: 0,
            borderRight: "1px solid #e5e7eb",
            background: "#f8fafc",
            display: "flex",
            flexDirection: "column",
          }}>
            <div style={{ padding: "16px 16px 8px", fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.6, textTransform: "uppercase" }}>
              Folders
            </div>
            <div style={{ padding: "0 10px 16px", flex: 1, overflowY: "auto" }}>
              <button
                type="button"
                onClick={() => this.setState({ selectedLibraryFolder: null })}
                style={folderItemStyle(selectedLibraryFolder === null)}
              >
                <span>📦 All items</span>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>{videoLibrary.length}</span>
              </button>
              <button
                type="button"
                onClick={() => this.setState({ selectedLibraryFolder: "" })}
                style={folderItemStyle(selectedLibraryFolder === "")}
              >
                <span>🗂️ Unsorted</span>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>{unsortedCount}</span>
              </button>
              <div style={{ height: 1, background: "#e5e7eb", margin: "10px 6px" }} />
              {folderNames.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => this.setState({ selectedLibraryFolder: name })}
                  style={folderItemStyle(selectedLibraryFolder === name)}
                  title={name}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 170 }}>📁 {name}</span>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>{folderCounts.get(name)}</span>
                </button>
              ))}
              {folderNames.length === 0 && (
                <div style={{ fontSize: 12, color: "#94a3b8", padding: "8px 12px" }}>
                  No folders yet. Click "📁 Folder" on any tile to organize.
                </div>
              )}
            </div>
          </aside>

          {/* Main column */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 22px",
            borderBottom: "1px solid #e5e7eb",
          }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>📚 Community Video Library</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
                Reusable clips and images shared across this community.
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {videoLibrary.some((i) => i.type === "video") && (
                <button
                  type="button"
                  onClick={() => this.handleBackfillMissingThumbnails(missingThumbCount > 0 ? "missing" : "all")}
                  disabled={isBackfillingThumbnails}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 10,
                    background: "#fff",
                    border: "1px solid var(--color-accent-subtle)",
                    color: "var(--color-accent-active)",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: isBackfillingThumbnails ? "not-allowed" : "pointer",
                    opacity: isBackfillingThumbnails ? 0.6 : 1,
                  }}
                  title={missingThumbCount > 0
                    ? "Generate missing thumbnails for older uploads"
                    : "Re-generate every video thumbnail (skips fade-in frames)"}
                >
                  {isBackfillingThumbnails
                    ? `Generating ${backfillProgress.done}/${backfillProgress.total}…`
                    : missingThumbCount > 0
                      ? `🖼️ Generate ${missingThumbCount} thumbnail${missingThumbCount !== 1 ? "s" : ""}`
                      : "🔄 Regenerate thumbnails"}
                </button>
              )}
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: 10,
                  background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                + Upload
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={this.handleUploadToLibrary}
                  style={{ display: "none" }}
                />
              </label>
              <button
                type="button"
                onClick={() => this.setState({ isLibraryOpen: false, playingLibraryItemId: null })}
                style={{
                  padding: "8px 14px",
                  borderRadius: 10,
                  background: "#fff",
                  border: "1px solid #d1d5db",
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>

          {libraryUploads.length > 0 && (
            <div style={{ padding: "12px 22px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
                Uploading {libraryUploads.length} file{libraryUploads.length !== 1 ? "s" : ""}…
              </div>
              {libraryUploads.map((u) => (
                <div key={u.id} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#475569", marginBottom: 2 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 480 }}>{u.name}</span>
                    <span>{Math.floor(u.percent)}%</span>
                  </div>
                  <ProgressBar now={u.percent} style={{ height: 6 }} />
                </div>
              ))}
            </div>
          )}

          <div
            style={{ flex: 1, overflowY: "auto", padding: 22, position: "relative" }}
            onDragOver={(e) => {
              e.preventDefault();
              if (!this.state.isDraggingOverLibrary) this.setState({ isDraggingOverLibrary: true });
            }}
            onDragLeave={(e) => {
              if (e.currentTarget === e.target) this.setState({ isDraggingOverLibrary: false });
            }}
            onDrop={this.handleLibraryDrop}
          >
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 16,
              flexWrap: "wrap",
            }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                  {folderHeading}
                </div>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {sortedItems.length} item{sortedItems.length !== 1 ? "s" : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="search"
                  placeholder="🔍 Search by name…"
                  value={librarySearch}
                  onChange={(e) => this.setState({ librarySearch: e.target.value })}
                  style={{
                    padding: "7px 12px",
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    fontSize: 13,
                    outline: "none",
                    width: 220,
                  }}
                />
                <select
                  value={librarySortBy}
                  onChange={(e) => this.setState({ librarySortBy: e.target.value as State["librarySortBy"] })}
                  style={{
                    padding: "7px 10px",
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="name">Name (A–Z)</option>
                  <option value="usage">Most used in reel</option>
                </select>
              </div>
            </div>

            {selectedCount > 0 && (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                marginBottom: 14,
                borderRadius: 10,
                background: "var(--color-accent-subtle)",
                border: "1px solid var(--color-accent-subtle)",
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-accent-active)" }}>
                  {selectedCount} selected
                </span>
                <button
                  type="button"
                  onClick={() => this.selectAllFilteredLibraryItems(sortedItems)}
                  style={{
                    padding: "5px 10px",
                    borderRadius: 8,
                    background: "#fff",
                    border: "1px solid var(--color-accent-subtle)",
                    color: "var(--color-accent-active)",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Select all in view
                </button>
                <button
                  type="button"
                  onClick={() => this.setState({ bulkFolderPickerOpen: true })}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 8,
                    background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
                    border: "none",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  📁 Move to folder…
                </button>
                <button
                  type="button"
                  onClick={this.handleBulkDelete}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 8,
                    background: "#fef2f2",
                    border: "1px solid #fca5a5",
                    color: "#dc2626",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  🗑 Delete
                </button>
                <button
                  type="button"
                  onClick={this.clearLibrarySelection}
                  style={{
                    marginLeft: "auto",
                    padding: "5px 10px",
                    borderRadius: 8,
                    background: "transparent",
                    border: "none",
                    color: "#64748b",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              </div>
            )}

            {isDraggingOverLibrary && (
              <div style={{
                position: "absolute",
                inset: 8,
                border: "3px dashed var(--color-accent-active)",
                background: "rgba(238, 242, 255, 0.85)",
                borderRadius: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--color-accent-active)",
                fontSize: 18,
                fontWeight: 700,
                pointerEvents: "none",
                zIndex: 1,
              }}>
                Drop files to upload to {folderHeading === "All items" ? "library (Unsorted)" : folderHeading}
              </div>
            )}

            {sortedItems.length === 0 && libraryUploads.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#64748b" }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>🎞️</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#334155", marginBottom: 4 }}>
                  {videoLibrary.length === 0
                    ? "Your library is empty"
                    : `No items in "${folderHeading}" yet`}
                </div>
                <div style={{ fontSize: 13 }}>
                  {videoLibrary.length === 0
                    ? "Upload images or videos to build a reusable pool of faceless content."
                    : selectedLibraryFolder
                      ? "Click the 📁 tag on any tile in 'All items' to move it here."
                      : "Pick a folder in the sidebar or upload new media."}
                </div>
              </div>
            )}

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 14,
            }}>
              {sortedItems.map((item) => (
                <div
                  key={item.id}
                  style={{
                    border: selectedLibraryItemIds[item.id]
                      ? "2px solid var(--color-accent-active)"
                      : "1px solid #e5e7eb",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "#fff",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "9 / 16",
                    background: "#0f172a",
                    overflow: "hidden",
                  }}>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); this.toggleLibraryItemSelected(item.id); }}
                      title={selectedLibraryItemIds[item.id] ? "Deselect" : "Select"}
                      style={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        border: selectedLibraryItemIds[item.id]
                          ? "2px solid var(--color-accent-active)"
                          : "2px solid rgba(255, 255, 255, 0.85)",
                        background: selectedLibraryItemIds[item.id]
                          ? "var(--color-accent-active)"
                          : "rgba(15, 23, 42, 0.4)",
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: "pointer",
                        zIndex: 2,
                        padding: 0,
                        lineHeight: 1,
                      }}
                    >
                      {selectedLibraryItemIds[item.id] ? "✓" : ""}
                    </button>
                    {item.type === "image" ? (
                      <img
                        src={item.mediaThumbnail || item.mediaUrl}
                        alt={item.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : playingLibraryItemId === item.id ? (
                      <video
                        src={item.mediaUrl}
                        controls
                        autoPlay
                        playsInline
                        preload="auto"
                        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", background: "#000" }}
                      />
                    ) : (
                      <div
                        onClick={() => this.setState({ playingLibraryItemId: item.id })}
                        style={{ width: "100%", height: "100%", cursor: "pointer", position: "relative" }}
                      >
                        {item.mediaThumbnail && item.mediaThumbnail.startsWith("data:") ? (
                          <img
                            src={item.mediaThumbnail}
                            alt={item.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                        ) : (
                          <video
                            // Seek past fade-in so the poster isn't a black frame
                            src={`${item.mediaUrl}${item.mediaUrl.includes("#") ? "" : "#t=1.5"}`}
                            muted
                            playsInline
                            preload="auto"
                            onLoadedMetadata={(e) => {
                              const v = e.currentTarget as HTMLVideoElement;
                              const duration = Number.isFinite(v.duration) ? v.duration : 4;
                              const target = Math.min(2.0, Math.max(0.3, duration / 2));
                              try { v.currentTime = target; } catch { /* noop */ }
                            }}
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", background: "#0f172a" }}
                          />
                        )}
                        <div style={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(0, 0, 0, 0.15)",
                        }}>
                          <div style={{
                            width: 52,
                            height: 52,
                            borderRadius: "50%",
                            background: "rgba(0, 0, 0, 0.65)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: 22,
                            paddingLeft: 5,
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.35)",
                          }}>
                            ▶
                          </div>
                        </div>
                      </div>
                    )}
                    <span style={{
                      position: "absolute",
                      top: 6,
                      left: 6,
                      background: item.type === "video" ? "rgba(102, 126, 234, 0.9)" : "rgba(118, 75, 162, 0.9)",
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "2px 6px",
                      borderRadius: 4,
                      textTransform: "uppercase",
                    }}>
                      {item.type}
                    </span>
                    {item.durationSec !== undefined && (
                      <span style={{
                        position: "absolute",
                        bottom: 6,
                        right: 6,
                        background: "rgba(15, 23, 42, 0.8)",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 600,
                        padding: "2px 6px",
                        borderRadius: 4,
                      }}>
                        {item.durationSec.toFixed(1)}s
                      </span>
                    )}
                  </div>
                  <div style={{ padding: "8px 10px", flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#1f2937",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }} title={item.name}>
                      {item.name}
                    </div>
                    <button
                      type="button"
                      onClick={() => this.handleOpenFolderPicker(item)}
                      title="Change folder"
                      style={{
                        alignSelf: "flex-start",
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: item.folder ? "var(--color-accent-subtle)" : "#f1f5f9",
                        border: item.folder ? "1px solid var(--color-accent-subtle)" : "1px solid #e2e8f0",
                        color: item.folder ? "var(--color-accent-active)" : "#64748b",
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.folder ? `📁 ${item.folder}` : "📁 Add to folder"}
                    </button>
                    <div style={{ display: "flex", gap: 6, marginTop: "auto" }}>
                      {(() => {
                        const usageCount = segmentUsageByLibraryId.get(item.id) || 0;
                        const isAdded = usageCount > 0;
                        return (
                          <button
                            type="button"
                            onClick={() => this.handleAddSegmentFromLibrary(item)}
                            title={isAdded ? "Click to add another copy to the reel" : "Add to reel"}
                            style={{
                              flex: 1,
                              padding: "6px 8px",
                              borderRadius: 8,
                              background: isAdded
                                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                                : "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
                              color: "#fff",
                              border: "none",
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                              transition: "background 0.15s",
                            }}
                          >
                            {isAdded ? `✓ Added${usageCount > 1 ? ` ×${usageCount}` : ""}` : "+ Add"}
                          </button>
                        );
                      })()}
                      <button
                        type="button"
                        onClick={() => this.handleRemoveLibraryItem(item)}
                        title="Delete from library"
                        style={{
                          padding: "6px 8px",
                          borderRadius: 8,
                          background: "#fef2f2",
                          border: "1px solid #fca5a5",
                          color: "#dc2626",
                          fontSize: 12,
                          cursor: "pointer",
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
        {this.renderFolderPicker(folderNames)}
        {this.renderBulkFolderPicker(folderNames, selectedCount)}
      </div>
    );

    if (typeof document === "undefined") return overlay;
    return ReactDOM.createPortal(overlay, document.body);
  };

  renderBulkFolderPicker = (folderNames: string[], selectedCount: number) => {
    if (!this.state.bulkFolderPickerOpen) return null;
    const rowStyle: React.CSSProperties = {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      textAlign: "left",
      padding: "10px 12px",
      borderRadius: 8,
      background: "transparent",
      border: "none",
      color: "#1f2937",
      fontWeight: 500,
      fontSize: 13,
      cursor: "pointer",
      marginBottom: 2,
    };

    return (
      <div
        onClick={(e) => { e.stopPropagation(); this.setState({ bulkFolderPickerOpen: false }); }}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.45)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff",
            borderRadius: 14,
            width: "100%",
            maxWidth: 380,
            maxHeight: "70vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.35)",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
              Move {selectedCount} item{selectedCount !== 1 ? "s" : ""} to folder
            </div>
          </div>
          <div style={{ padding: 8, flex: 1, overflowY: "auto" }}>
            <button type="button" onClick={() => this.handleBulkAssignFolder(null)} style={rowStyle}>
              <span>🗂️ Unsorted</span>
            </button>
            {folderNames.length > 0 && (
              <div style={{ height: 1, background: "#e5e7eb", margin: "6px 6px" }} />
            )}
            {folderNames.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => this.handleBulkAssignFolder(name)}
                style={rowStyle}
                title={name}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}>
                  📁 {name}
                </span>
              </button>
            ))}
            <div style={{ height: 1, background: "#e5e7eb", margin: "6px 6px" }} />
            <button
              type="button"
              onClick={this.handleBulkAssignNewFolder}
              style={{ ...rowStyle, color: "var(--color-accent-active)", fontWeight: 600 }}
            >
              <span>＋ New folder…</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  renderSegmentVariationsPanel = (seg: FreeFlowSegment) => {
    const expanded = this.state.expandedVariationsForSegId === seg.id;
    const textVars = seg.captionVariations || [];
    const clipVars = seg.clipVariations || [];
    const folderPoolSize = this.getFolderPoolForSegment(seg).length;
    const totalTextOpts = 1 + textVars.filter((t) => t.trim() !== "").length;
    const totalClipOpts = 1 + clipVars.length + folderPoolSize;
    const totalSegCombos = totalTextOpts * totalClipOpts;

    return (
      <div style={{ borderTop: expanded ? "1px solid #e5e7eb" : "none" }}>
        <button
          type="button"
          onClick={() =>
            this.setState((prev) => ({
              expandedVariationsForSegId: prev.expandedVariationsForSegId === seg.id ? null : seg.id,
            }))
          }
          style={{
            width: "100%",
            padding: "8px 14px",
            background: expanded ? "#f1f5f9" : "transparent",
            border: "none",
            borderTop: expanded ? "none" : "1px solid #e5e7eb",
            color: "#475569",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            textAlign: "left",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{expanded ? "▼" : "▶"} Variations {totalSegCombos > 1 ? `(${totalSegCombos} options)` : ""}</span>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>
            {totalTextOpts > 1 || totalClipOpts > 1
              ? `${totalTextOpts} text × ${totalClipOpts} clip`
              : "click to add"}
          </span>
        </button>
        {expanded && (
          <div style={{ padding: "12px 14px 14px", display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Text variations */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", letterSpacing: 0.4, textTransform: "uppercase" }}>
                  Text alternatives
                </span>
                <button
                  type="button"
                  onClick={() => this.handleAddTextVariation(seg.id)}
                  style={{
                    padding: "3px 10px",
                    borderRadius: 999,
                    background: "var(--color-accent-subtle)",
                    border: "1px solid var(--color-accent-subtle)",
                    color: "var(--color-accent-active)",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  + Add text
                </button>
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 6 }}>
                Primary: <em>"{seg.caption || "(empty)"}"</em>
              </div>
              {textVars.length === 0 && (
                <div style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>
                  No alternates yet. Each batch render picks one at random.
                </div>
              )}
              {textVars.map((text, i) => (
                <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  <input
                    type="text"
                    placeholder={`Alternative ${i + 1}`}
                    value={text}
                    onChange={(e) => this.handleUpdateTextVariation(seg.id, i, e.target.value)}
                    style={{
                      flex: 1,
                      padding: "6px 10px",
                      borderRadius: 8,
                      border: "1px solid #d1d5db",
                      fontSize: 12,
                      outline: "none",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => this.handleRemoveTextVariation(seg.id, i)}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      background: "#fef2f2",
                      border: "1px solid #fca5a5",
                      color: "#dc2626",
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {/* Clip variations */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", letterSpacing: 0.4, textTransform: "uppercase" }}>
                  Clip alternatives
                </span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <select
                    value={seg.clipFolderPool || ""}
                    onChange={(e) => this.handleUpdateFreeFlowSegment(seg.id, {
                      clipFolderPool: e.target.value || undefined,
                    })}
                    title="Sample at random from every clip in this folder"
                    style={{
                      padding: "3px 8px",
                      borderRadius: 999,
                      background: seg.clipFolderPool ? "#dcfce7" : "#fff",
                      border: seg.clipFolderPool ? "1px solid #86efac" : "1px solid #d1d5db",
                      color: seg.clipFolderPool ? "#166534" : "#475569",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    <option value="">📂 From folder…</option>
                    {(() => {
                      const folderSet = new Set<string>();
                      for (const i of this.state.videoLibrary) {
                        if (i.folder) folderSet.add(i.folder);
                      }
                      return Array.from(folderSet).sort().map((name) => {
                        const count = this.state.videoLibrary.filter((i) => i.folder === name).length;
                        return (
                          <option key={name} value={name}>
                            📁 {name} ({count})
                          </option>
                        );
                      });
                    })()}
                  </select>
                  <button
                    type="button"
                    onClick={() => this.setState({ clipPickerForSegId: seg.id })}
                    style={{
                      padding: "3px 10px",
                      borderRadius: 999,
                      background: "var(--color-accent-subtle)",
                      border: "1px solid var(--color-accent-subtle)",
                      color: "var(--color-accent-active)",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    + Add clip
                  </button>
                </div>
              </div>
              {seg.clipFolderPool && (
                <div style={{
                  fontSize: 11,
                  color: "#166534",
                  background: "#dcfce7",
                  border: "1px solid #86efac",
                  padding: "6px 10px",
                  borderRadius: 8,
                  marginBottom: 6,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                  <span>
                    🎲 Will randomize from {this.getFolderPoolForSegment(seg).length} clip
                    {this.getFolderPoolForSegment(seg).length !== 1 ? "s" : ""} in <strong>📁 {seg.clipFolderPool}</strong>
                  </span>
                  <button
                    type="button"
                    onClick={() => this.handleUpdateFreeFlowSegment(seg.id, { clipFolderPool: undefined })}
                    style={{
                      padding: "2px 6px",
                      borderRadius: 6,
                      background: "transparent",
                      border: "none",
                      color: "#166534",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <div style={{
                  width: 50, height: 88, borderRadius: 6, overflow: "hidden", background: "#e5e7eb",
                  position: "relative", border: "2px solid var(--color-accent-active)",
                }}>
                  {seg.mediaThumbnail ? (
                    <img src={seg.mediaThumbnail} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>🎬</div>
                  )}
                  <span style={{
                    position: "absolute", bottom: 0, left: 0, right: 0,
                    background: "rgba(67, 56, 202, 0.85)", color: "#fff",
                    fontSize: 9, fontWeight: 700, textAlign: "center", padding: 2,
                  }}>
                    primary
                  </span>
                </div>
                {clipVars.map((v, i) => (
                  <div key={i} style={{
                    width: 50, height: 88, borderRadius: 6, overflow: "hidden", background: "#e5e7eb",
                    position: "relative",
                  }}>
                    {v.mediaThumbnail ? (
                      <img src={v.mediaThumbnail} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>🎬</div>
                    )}
                    <button
                      type="button"
                      onClick={() => this.handleRemoveClipVariation(seg.id, i)}
                      style={{
                        position: "absolute", top: 2, right: 2,
                        width: 16, height: 16,
                        padding: 0,
                        borderRadius: "50%",
                        background: "rgba(220, 38, 38, 0.9)",
                        border: "none",
                        color: "#fff",
                        fontSize: 10,
                        cursor: "pointer",
                        lineHeight: 1,
                      }}
                      title="Remove this alternative"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  renderClipPicker = () => {
    const { clipPickerForSegId, videoLibrary } = this.state;
    if (!clipPickerForSegId) return null;
    const seg = this.state.freeFlowSegments.find((s) => s.id === clipPickerForSegId);
    if (!seg) return null;

    const sortedItems = [...videoLibrary]
      .filter((i) => i.id !== seg.libraryItemId)
      .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));

    return (
      <div
        onClick={() => this.setState({ clipPickerForSegId: null })}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.6)",
          zIndex: 10001,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff",
            borderRadius: 14,
            width: "100%",
            maxWidth: 720,
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "14px 18px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Pick an alternative clip</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                Will be picked at random during batch render.
              </div>
            </div>
            <button
              type="button"
              onClick={() => this.setState({ clipPickerForSegId: null })}
              style={{
                padding: "6px 12px",
                borderRadius: 8,
                background: "#fff",
                border: "1px solid #d1d5db",
                color: "#374151",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
            {sortedItems.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "#64748b", fontSize: 13 }}>
                No other clips in the library yet. Upload more in 📚 Library to use them as alternates.
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
                gap: 10,
              }}>
                {sortedItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => this.handleAddClipVariation(seg.id, item)}
                    style={{
                      padding: 0,
                      borderRadius: 8,
                      background: "#fff",
                      border: "1px solid #e5e7eb",
                      cursor: "pointer",
                      overflow: "hidden",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ width: "100%", aspectRatio: "9 / 16", background: "#0f172a", position: "relative" }}>
                      {item.mediaThumbnail && item.mediaThumbnail.startsWith("data:") ? (
                        <img src={item.mediaThumbnail} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <img src={item.mediaThumbnail || item.mediaUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      )}
                    </div>
                    <div style={{
                      padding: "6px 8px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#1f2937",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }} title={item.name}>
                      {item.name}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  renderFolderPicker = (folderNames: string[]) => {
    const { folderPickerForItemId, videoLibrary } = this.state;
    if (!folderPickerForItemId) return null;
    const item = videoLibrary.find((i) => i.id === folderPickerForItemId);
    if (!item) return null;

    const rowStyle = (active: boolean): React.CSSProperties => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      textAlign: "left",
      padding: "10px 12px",
      borderRadius: 8,
      background: active ? "var(--color-accent-subtle)" : "transparent",
      border: "none",
      color: active ? "var(--color-accent-active)" : "#1f2937",
      fontWeight: active ? 700 : 500,
      fontSize: 13,
      cursor: "pointer",
      marginBottom: 2,
    });

    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          this.setState({ folderPickerForItemId: null });
        }}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.45)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff",
            borderRadius: 14,
            width: "100%",
            maxWidth: 380,
            maxHeight: "70vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.35)",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Move to folder</div>
            <div style={{
              fontSize: 12,
              color: "#64748b",
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }} title={item.name}>
              {item.name}
            </div>
          </div>
          <div style={{ padding: 8, flex: 1, overflowY: "auto" }}>
            <button
              type="button"
              onClick={() => this.handleCommitFolderForItem(item, null)}
              style={rowStyle(!item.folder)}
            >
              <span>🗂️ Unsorted</span>
              {!item.folder && <span style={{ fontSize: 11 }}>✓</span>}
            </button>
            {folderNames.length > 0 && (
              <div style={{ height: 1, background: "#e5e7eb", margin: "6px 6px" }} />
            )}
            {folderNames.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => this.handleCommitFolderForItem(item, name)}
                style={rowStyle(item.folder === name)}
                title={name}
              >
                <span style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 280,
                }}>📁 {name}</span>
                {item.folder === name && <span style={{ fontSize: 11 }}>✓</span>}
              </button>
            ))}
            <div style={{ height: 1, background: "#e5e7eb", margin: "6px 6px" }} />
            <button
              type="button"
              onClick={() => this.handleCommitNewFolderForItem(item)}
              style={{
                ...rowStyle(false),
                color: "var(--color-accent-active)",
                fontWeight: 600,
              }}
            >
              <span>＋ New folder…</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  renderFreeFlowModeTabs = () => {
    const { freeflowBuilderMode } = this.state;
    const switchToMode = (mode: State["freeflowBuilderMode"]) => {
      this.setState((prev) => {
        const next: Partial<State> = { freeflowBuilderMode: mode };
        if (mode === "combinations" && prev.combinationPools.length === 0) {
          next.combinationPools = [
            { id: `pool-${Date.now()}-a`, folder: "", durationSec: 3, caption: "" },
            { id: `pool-${Date.now()}-b`, folder: "", durationSec: 5, caption: "" },
          ];
        }
        return next as State;
      });
    };
    const tab = (mode: State["freeflowBuilderMode"], label: string, hint: string) => (
      <button
        type="button"
        onClick={() => switchToMode(mode)}
        style={{
          flex: 1,
          padding: "12px 14px",
          background: freeflowBuilderMode === mode ? "#fff" : "#f8fafc",
          border: "1px solid #e5e7eb",
          borderBottom: freeflowBuilderMode === mode ? "1px solid #fff" : "1px solid #e5e7eb",
          borderRadius: "10px 10px 0 0",
          color: freeflowBuilderMode === mode ? "#0f172a" : "#64748b",
          fontWeight: 700,
          fontSize: 14,
          cursor: "pointer",
          textAlign: "left",
          marginRight: 4,
          position: "relative",
          top: 1,
        }}
      >
        <div>{label}</div>
        <div style={{ fontSize: 11, fontWeight: 500, color: freeflowBuilderMode === mode ? "#64748b" : "#94a3b8", marginTop: 2 }}>
          {hint}
        </div>
      </button>
    );

    return (
      <div style={{ display: "flex", marginBottom: -1, marginTop: 4 }}>
        {tab("combinations", "🎯 Combinations", "Mass-produce hook × demo combos")}
        {tab("manual", "🛠 Manual", "Hand-craft one reel at a time")}
      </div>
    );
  };

  ensureDefaultCombinationPools = () => {
    if (this.state.combinationPools.length === 0) {
      this.setState({
        combinationPools: [
          { id: `pool-${Date.now()}-a`, folder: "", durationSec: 3, caption: "" },
          { id: `pool-${Date.now()}-b`, folder: "", durationSec: 5, caption: "" },
        ],
      });
    }
  };

  renderCombinationsBuilder = () => {
    const { combinationPools, isBatchRendering, videoLibrary, batchProgress } = this.state;
    const { pools, total, missingFolder, emptyFolder } = this.computeCombinationTotals();
    const folderSet = new Set<string>();
    for (const i of videoLibrary) {
      if (i.folder) folderSet.add(i.folder);
    }
    const allFolders = Array.from(folderSet).sort();

    const totalDurationSec = combinationPools.reduce((s, p) => s + p.durationSec, 0);
    const estimateMin = (totalDurationSec * total * 1.5) / 60;

    if (allFolders.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "40px 20px", background: "#f9fafb", borderRadius: 12, border: "2px dashed #d1d5db" }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📁</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
            No folders yet
          </div>
          <p style={{ fontSize: 13, color: "#6b7280", margin: 0, marginBottom: 10 }}>
            Open the Library and put your clips into folders like "Hooks" and "Demos" first.
          </p>
          <button
            type="button"
            onClick={() => this.setState({ isLibraryOpen: true })}
            style={{
              padding: "8px 16px",
              borderRadius: 10,
              background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
              color: "#fff",
              border: "none",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            📚 Open Library
          </button>
        </div>
      );
    }

    return (
      <div>
        <div style={{
          background: "var(--color-accent-subtle)",
          border: "1px solid var(--color-accent-subtle)",
          padding: "10px 14px",
          borderRadius: 10,
          marginBottom: 14,
          fontSize: 13,
          color: "#3730a3",
        }}>
          <strong>How this works:</strong> add one "slot" per chunk of your reel (e.g. Slot 1 = Hook, Slot 2 = Demo).
          Pick a folder for each slot. Every clip in slot 1 will pair with every clip in slot 2 — you get the full matrix.
        </div>

        {combinationPools.length === 0 && (
          <div style={{ textAlign: "center", padding: "30px 20px", background: "#f9fafb", borderRadius: 12, border: "2px dashed #d1d5db", marginBottom: 14 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🎬</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
              Add your first slot
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
              A slot binds one piece of every reel to a folder of clips.
            </p>
          </div>
        )}

        {combinationPools.map((p, index) => {
          const items = videoLibrary.filter((i) => (i.folder || "") === p.folder);
          return (
            <div
              key={p.id}
              style={{
                marginBottom: 10,
                padding: 14,
                background: "#f9fafb",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
                  color: "#fff", fontWeight: 700, fontSize: 14,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  {index + 1}
                </div>
                <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#1f2937" }}>
                  Slot {index + 1}
                  {p.folder && items.length > 0 && (
                    <span style={{ color: "#64748b", fontWeight: 500 }}>
                      {" "}— {items.length} clip{items.length !== 1 ? "s" : ""} available
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => this.handleMoveCombinationPool(p.id, "up")}
                  disabled={index === 0}
                  style={{
                    padding: "4px 8px", borderRadius: 6,
                    border: "1px solid #d1d5db", background: "#fff",
                    cursor: index === 0 ? "not-allowed" : "pointer",
                    opacity: index === 0 ? 0.4 : 1, fontSize: 13,
                  }}
                >
                  ▲
                </button>
                <button
                  type="button"
                  onClick={() => this.handleMoveCombinationPool(p.id, "down")}
                  disabled={index === combinationPools.length - 1}
                  style={{
                    padding: "4px 8px", borderRadius: 6,
                    border: "1px solid #d1d5db", background: "#fff",
                    cursor: index === combinationPools.length - 1 ? "not-allowed" : "pointer",
                    opacity: index === combinationPools.length - 1 ? 0.4 : 1, fontSize: 13,
                  }}
                >
                  ▼
                </button>
                <button
                  type="button"
                  onClick={() => this.handleRemoveCombinationPool(p.id)}
                  style={{
                    padding: "4px 8px", borderRadius: 6,
                    border: "1px solid #fca5a5", background: "#fef2f2",
                    color: "#dc2626", fontSize: 13, cursor: "pointer",
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                <label style={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>Folder:</label>
                <select
                  value={p.folder}
                  onChange={(e) => this.handleUpdateCombinationPool(p.id, { folder: e.target.value })}
                  style={{
                    flex: 1,
                    minWidth: 180,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: items.length === 0 ? "1px solid #fca5a5" : "1px solid #d1d5db",
                    fontSize: 13,
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  <option value="">-- pick a folder --</option>
                  {allFolders.map((name) => {
                    const count = videoLibrary.filter((i) => i.folder === name).length;
                    return (
                      <option key={name} value={name}>
                        📁 {name} ({count})
                      </option>
                    );
                  })}
                </select>
                <label style={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>Duration:</label>
                <input
                  type="number"
                  min={0.5}
                  max={30}
                  step={0.5}
                  value={p.durationSec}
                  onChange={(e) =>
                    this.handleUpdateCombinationPool(p.id, {
                      durationSec: Math.max(0.5, parseFloat(e.target.value) || 0.5),
                    })
                  }
                  style={{
                    width: 70,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid #d1d5db",
                    fontSize: 13,
                    textAlign: "center",
                  }}
                />
                <span style={{ fontSize: 12, color: "#6b7280" }}>sec</span>
              </div>

              <input
                type="text"
                placeholder="Caption to overlay on every clip in this slot (optional)"
                value={p.caption}
                onChange={(e) => this.handleUpdateCombinationPool(p.id, { caption: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  fontSize: 13,
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
            </div>
          );
        })}

        <button
          type="button"
          onClick={this.handleAddCombinationPool}
          style={{
            width: "100%",
            padding: "10px 14px",
            borderRadius: 10,
            background: "#fff",
            border: "1px dashed #d1d5db",
            color: "#475569",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            marginBottom: 16,
          }}
        >
          + Add slot
        </button>

        {combinationPools.length > 0 && (
          <div style={{
            padding: 16,
            background: "#fafafa",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                  {missingFolder || emptyFolder
                    ? "Pick a non-empty folder for every slot"
                    : <>🎲 {total} total combination{total !== 1 ? "s" : ""}</>
                  }
                </div>
                {!missingFolder && !emptyFolder && (
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {pools.map((p, i) => `${p.items.length}`).join(" × ")} = {total}
                    {total > 64 && " — will render the first 64 only"}
                    {totalDurationSec > 0 && ` • ~${estimateMin.toFixed(1)} min`}
                  </div>
                )}
              </div>
            </div>
            {isBatchRendering ? (
              <div>
                <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>
                  Rendered {batchProgress.done}/{batchProgress.total}
                </div>
                <ProgressBar now={(batchProgress.done / Math.max(1, batchProgress.total)) * 100} style={{ height: 8 }} />
                <button
                  type="button"
                  onClick={this.handleCancelBatchRender}
                  style={{
                    marginTop: 10,
                    padding: "8px 14px",
                    borderRadius: 10,
                    background: "#fff",
                    border: "1px solid #fca5a5",
                    color: "#dc2626",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  Cancel after current
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  disabled={missingFolder || emptyFolder || total === 0}
                  onClick={() => this.handleRunCombinationBatch(total <= 32 ? "all" : "sample")}
                  style={{
                    padding: "12px 20px",
                    borderRadius: 10,
                    background: missingFolder || emptyFolder || total === 0
                      ? "#e5e7eb"
                      : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: missingFolder || emptyFolder || total === 0 ? "#9ca3af" : "#fff",
                    border: "none",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: missingFolder || emptyFolder || total === 0 ? "not-allowed" : "pointer",
                  }}
                >
                  {total <= 32
                    ? `🎬 Render all ${total} combination${total !== 1 ? "s" : ""}`
                    : `🎲 Render 16 random of ${total} combinations`}
                </button>
                {total > 32 && (
                  <button
                    type="button"
                    onClick={() => this.handleRunCombinationBatch("all")}
                    style={{
                      padding: "10px 14px",
                      borderRadius: 10,
                      background: "#fff",
                      border: "1px solid var(--color-accent-subtle)",
                      color: "var(--color-accent-active)",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                    title="Force render up to the 64-combination cap"
                  >
                    Or render first 64
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  renderFreeFlowComposer = () => {
    const { freeFlowSegments, isRenderingReel, freeflowBuilderMode } = this.state;
    const totalDuration = freeFlowSegments.reduce((sum, s) => sum + s.duration, 0);

    if (freeflowBuilderMode === "combinations") {
      return (
        <div>
          {this.renderVideoLibraryModal()}
          {this.renderClipPicker()}
          {this.renderFreeFlowModeTabs()}
          <div style={{
            padding: 18,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderTopLeftRadius: 0,
            borderRadius: "0 10px 10px 10px",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                🎯 Combination Builder
              </div>
              {this.props.communityId && (
                <button
                  type="button"
                  onClick={() => this.setState({ isLibraryOpen: true })}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 10,
                    background: "#fff",
                    border: "1px solid #d1d5db",
                    color: "#374151",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  📚 Library
                </button>
              )}
            </div>
            {this.renderCombinationsBuilder()}
            {this.state.batchResults.length > 0 && (
              <div style={{ marginTop: 22 }}>
                {this.renderBatchResultsGrid()}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div>
        {this.renderVideoLibraryModal()}
        {this.renderClipPicker()}
        {this.renderFreeFlowModeTabs()}
        <div style={{
          padding: 18,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderTopLeftRadius: 0,
          borderRadius: "0 10px 10px 10px",
          marginBottom: 14,
        }}>
        {/* Total Duration & Add Button */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>
              {freeFlowSegments.length} segment{freeFlowSegments.length !== 1 ? "s" : ""}
            </span>
            {freeFlowSegments.length > 0 && (
              <span style={{ fontSize: 13, color: "#6b7280", marginLeft: 8 }}>
                Total: {totalDuration.toFixed(1)}s
              </span>
            )}
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {this.props.communityId && (
              <button
                type="button"
                onClick={() => this.setState({ isLibraryOpen: true })}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: 10,
                  background: "#fff",
                  border: "1px solid #d1d5db",
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                <span role="img" aria-label="library">📚</span>
                Library
                {this.state.videoLibrary.length > 0 && (
                  <span style={{
                    background: "var(--color-accent-subtle)",
                    color: "var(--color-accent-active)",
                    padding: "1px 7px",
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                  }}>
                    {this.state.videoLibrary.length}
                  </span>
                )}
              </button>
            )}
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 10,
                background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              + Add Media
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={this.handleAddFreeFlowSegments}
                style={{ display: "none" }}
              />
            </label>
          </div>
        </div>

        {/* Empty State */}
        {freeFlowSegments.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 20px", background: "#f9fafb", borderRadius: 12, border: "2px dashed #d1d5db" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📁</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
              Add images and videos
            </div>
            <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
              Upload media or pick from your community library to compose a free flow reel.
            </p>
            {this.props.communityId && this.state.videoLibrary.length > 0 && (
              <button
                type="button"
                onClick={() => this.setState({ isLibraryOpen: true })}
                style={{
                  marginTop: 14,
                  padding: "8px 14px",
                  borderRadius: 10,
                  background: "#fff",
                  border: "1px solid #d1d5db",
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                📚 Open Library ({this.state.videoLibrary.length})
              </button>
            )}
          </div>
        )}

        {/* Segment List */}
        {freeFlowSegments.map((seg, index) => {
          const isDragging = this.state.draggingSegmentId === seg.id;
          const isDropTarget = this.state.dropTargetSegmentId === seg.id && !isDragging;
          return (
          <div
            key={seg.id}
            onDragOver={this.handleSegmentDragOver(seg.id)}
            onDrop={this.handleSegmentDrop(seg.id)}
            style={{
              marginBottom: 8,
              background: "#f9fafb",
              borderRadius: 12,
              border: isDropTarget ? "2px solid var(--color-accent-active)" : "1px solid #e5e7eb",
              overflow: "hidden",
              opacity: isDragging ? 0.4 : 1,
              transition: "opacity 0.15s, border-color 0.15s",
            }}
          >
          <div
            style={{
              display: "flex",
              gap: 12,
              padding: 12,
              alignItems: "flex-start",
            }}
          >
            <div
              draggable
              onDragStart={this.handleSegmentDragStart(seg.id)}
              onDragEnd={this.handleSegmentDragEnd}
              title="Drag to reorder"
              style={{
                width: 16,
                alignSelf: "stretch",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "grab",
                color: "#94a3b8",
                fontSize: 16,
                userSelect: "none",
                flexShrink: 0,
              }}
            >
              ⋮⋮
            </div>
            {/* Thumbnail */}
            <div style={{ flexShrink: 0, width: 80, height: 142, borderRadius: 8, overflow: "hidden", background: "#e5e7eb", position: "relative" }}>
              {seg.type === "image" ? (
                <img src={seg.mediaUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : seg.mediaThumbnail ? (
                <img src={seg.mediaThumbnail} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>🎬</div>
              )}
              <span style={{
                position: "absolute",
                top: 4,
                left: 4,
                background: seg.type === "video" ? "rgba(102, 126, 234, 0.9)" : "rgba(118, 75, 162, 0.9)",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                padding: "2px 6px",
                borderRadius: 4,
                textTransform: "uppercase",
              }}>
                {seg.type}
              </span>
            </div>

            {/* Controls */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>#{index + 1}</span>
              </div>

              {/* Caption */}
              <input
                type="text"
                placeholder="Caption text (displayed on top)..."
                value={seg.caption}
                onChange={(e) => this.handleUpdateFreeFlowSegment(seg.id, { caption: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  fontSize: 13,
                  marginBottom: 8,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />

              {/* Duration */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>Duration:</label>
                <input
                  type="number"
                  min={0.1}
                  max={30}
                  step={0.1}
                  value={seg.duration}
                  onChange={(e) => {
                    const next = Math.max(0.1, parseFloat(e.target.value) || 0.1);
                    const updates: Partial<FreeFlowSegment> = { duration: next };
                    // Clamp text window if it now exceeds segment length
                    if (seg.captionEndSec !== undefined && seg.captionEndSec > next) {
                      updates.captionEndSec = next;
                    }
                    if (seg.captionStartSec !== undefined && seg.captionStartSec > next) {
                      updates.captionStartSec = Math.max(0, next - 0.1);
                    }
                    this.handleUpdateFreeFlowSegment(seg.id, updates);
                  }}
                  style={{
                    width: 70,
                    padding: "6px 8px",
                    borderRadius: 6,
                    border: "1px solid #d1d5db",
                    fontSize: 13,
                    textAlign: "center",
                    outline: "none",
                  }}
                />
                <span style={{ fontSize: 12, color: "#6b7280" }}>sec</span>
              </div>

              {/* Text timing — only when caption present */}
              {seg.caption.trim() !== "" && (() => {
                const start = seg.captionStartSec ?? 0;
                const end = seg.captionEndSec ?? seg.duration;
                const customized = seg.captionStartSec !== undefined || seg.captionEndSec !== undefined;
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <label style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>Text:</label>
                    <input
                      type="number"
                      min={0}
                      max={Math.max(0, seg.duration - 0.1)}
                      step={0.1}
                      value={Number(start.toFixed(2))}
                      onChange={(e) => {
                        const raw = parseFloat(e.target.value);
                        const safe = Number.isFinite(raw) ? raw : 0;
                        const nextStart = Math.min(Math.max(0, safe), Math.max(0, seg.duration - 0.1));
                        const nextEnd = Math.max(nextStart + 0.1, end);
                        this.handleUpdateFreeFlowSegment(seg.id, {
                          captionStartSec: nextStart,
                          captionEndSec: Math.min(nextEnd, seg.duration),
                        });
                      }}
                      style={{
                        width: 64,
                        padding: "6px 8px",
                        borderRadius: 6,
                        border: "1px solid #d1d5db",
                        fontSize: 13,
                        textAlign: "center",
                        outline: "none",
                      }}
                    />
                    <span style={{ fontSize: 12, color: "#6b7280" }}>→</span>
                    <input
                      type="number"
                      min={0.1}
                      max={seg.duration}
                      step={0.1}
                      value={Number(end.toFixed(2))}
                      onChange={(e) => {
                        const raw = parseFloat(e.target.value);
                        const safe = Number.isFinite(raw) ? raw : seg.duration;
                        const nextEnd = Math.min(Math.max(0.1, safe), seg.duration);
                        const nextStart = Math.min(start, nextEnd - 0.1);
                        this.handleUpdateFreeFlowSegment(seg.id, {
                          captionStartSec: Math.max(0, nextStart),
                          captionEndSec: nextEnd,
                        });
                      }}
                      style={{
                        width: 64,
                        padding: "6px 8px",
                        borderRadius: 6,
                        border: "1px solid #d1d5db",
                        fontSize: 13,
                        textAlign: "center",
                        outline: "none",
                      }}
                    />
                    <span style={{ fontSize: 11, color: "#94a3b8" }}>sec</span>
                    {customized && (
                      <button
                        type="button"
                        onClick={() => this.handleUpdateFreeFlowSegment(seg.id, {
                          captionStartSec: undefined,
                          captionEndSec: undefined,
                        })}
                        title="Reset to full segment"
                        style={{
                          padding: "2px 8px",
                          borderRadius: 999,
                          background: "#f1f5f9",
                          border: "1px solid #e2e8f0",
                          color: "#64748b",
                          fontSize: 11,
                          cursor: "pointer",
                        }}
                      >
                        full
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Actions */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => this.handleMoveFreeFlowSegment(index, "up")}
                disabled={index === 0}
                style={{
                  padding: "4px 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  background: "#fff",
                  cursor: index === 0 ? "not-allowed" : "pointer",
                  opacity: index === 0 ? 0.4 : 1,
                  fontSize: 14,
                }}
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => this.handleMoveFreeFlowSegment(index, "down")}
                disabled={index === freeFlowSegments.length - 1}
                style={{
                  padding: "4px 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  background: "#fff",
                  cursor: index === freeFlowSegments.length - 1 ? "not-allowed" : "pointer",
                  opacity: index === freeFlowSegments.length - 1 ? 0.4 : 1,
                  fontSize: 14,
                }}
              >
                ▼
              </button>
              <button
                type="button"
                onClick={() => this.handleRemoveFreeFlowSegment(seg.id)}
                style={{
                  padding: "4px 8px",
                  border: "1px solid #fca5a5",
                  borderRadius: 6,
                  background: "#fef2f2",
                  cursor: "pointer",
                  fontSize: 14,
                  color: "#dc2626",
                }}
              >
                ✕
              </button>
            </div>
          </div>
          {this.renderSegmentVariationsPanel(seg)}
          </div>
          );
        })}

        {/* Render Button */}
        {freeFlowSegments.length > 0 && (
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <Button
              style={styles.primaryButton}
              onClick={this.renderFreeFlowReel}
              disabled={isRenderingReel}
            >
              {isRenderingReel ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Rendering...
                </>
              ) : (
                <>
                  <span role="img" aria-label="render">🎥</span>
                  Render Free Flow Reel
                </>
              )}
            </Button>
            <p style={{ marginTop: 8, fontSize: 13, color: "#6b7280" }}>
              {freeFlowSegments.length} segment{freeFlowSegments.length !== 1 ? "s" : ""} - {totalDuration.toFixed(1)}s total
            </p>
          </div>
        )}

        {freeFlowSegments.length > 0 && this.renderBatchSection()}
        </div>
      </div>
    );
  };

  renderBatchResultsGrid = () => {
    const { batchResults } = this.state;
    if (batchResults.length === 0) return null;
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", letterSpacing: 0.4, textTransform: "uppercase" }}>
            Results ({batchResults.length})
          </div>
          <button
            type="button"
            onClick={this.handleClearBatchResults}
            style={{
              padding: "5px 10px",
              borderRadius: 8,
              background: "#fff",
              border: "1px solid #e5e7eb",
              color: "#64748b",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Clear results
          </button>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
          gap: 10,
        }}>
          {batchResults.map((r, i) => (
            <div key={r.url} style={{ border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff", overflow: "hidden" }}>
              <div style={{ aspectRatio: "9 / 16", background: "#0f172a" }}>
                <video
                  src={r.url}
                  controls
                  preload="metadata"
                  playsInline
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>
              <div style={{ padding: "6px 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, color: "#64748b" }}>#{i + 1}</span>
                <a
                  href={r.url}
                  download={`reel-variation-${i + 1}.${r.ext || "webm"}`}
                  style={{ fontSize: 11, color: "var(--color-accent-active)", textDecoration: "none", fontWeight: 600 }}
                >
                  ⬇ {(r.ext || "webm").toUpperCase()}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  renderBatchSection = () => {
    const { batchCount, isBatchRendering, batchProgress, batchResults, freeFlowSegments } = this.state;
    const { fullMatrix } = this.computeBatchTotals(freeFlowSegments);
    const hasAnyVariations = freeFlowSegments.some(
      (s) =>
        (s.captionVariations?.some((t) => t.trim() !== "") || false) ||
        (s.clipVariations?.length || 0) > 0 ||
        (s.clipFolderPool ? this.getFolderPoolForSegment(s).length > 0 : false)
    );

    return (
      <div style={{
        marginTop: 24,
        padding: 16,
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        background: "#fafafa",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937" }}>
              🎲 Batch render
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {hasAnyVariations
                ? `Each batch picks 1 of ${fullMatrix} possible combinations at random.`
                : "Expand a segment and add text/clip alternatives to enable variations."}
            </div>
          </div>
          {batchResults.length > 0 && !isBatchRendering && (
            <button
              type="button"
              onClick={this.handleClearBatchResults}
              style={{
                padding: "5px 10px",
                borderRadius: 8,
                background: "#fff",
                border: "1px solid #e5e7eb",
                color: "#64748b",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Clear results
            </button>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>
            Variations:
          </label>
          <input
            type="number"
            min={1}
            max={64}
            step={1}
            value={batchCount}
            disabled={isBatchRendering}
            onChange={(e) => {
              const next = parseInt(e.target.value, 10);
              this.setState({ batchCount: Math.max(1, Math.min(64, Number.isFinite(next) ? next : 1)) });
            }}
            style={{
              width: 80,
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              fontSize: 13,
              textAlign: "center",
              outline: "none",
            }}
          />
          {!isBatchRendering ? (
            <button
              type="button"
              onClick={this.handleRunBatchRender}
              disabled={!hasAnyVariations}
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                background: hasAnyVariations
                  ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                  : "#e5e7eb",
                color: hasAnyVariations ? "#fff" : "#9ca3af",
                border: "none",
                fontSize: 13,
                fontWeight: 600,
                cursor: hasAnyVariations ? "pointer" : "not-allowed",
              }}
            >
              🎬 Render {batchCount} variation{batchCount !== 1 ? "s" : ""}
            </button>
          ) : (
            <button
              type="button"
              onClick={this.handleCancelBatchRender}
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                background: "#fff",
                border: "1px solid #fca5a5",
                color: "#dc2626",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel after current
            </button>
          )}
          <span style={{ fontSize: 12, color: "#94a3b8" }}>
            ~{(freeFlowSegments.reduce((s, x) => s + x.duration, 0) * batchCount).toFixed(0)}s render time
          </span>
        </div>

        {isBatchRendering && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>
              Rendered {batchProgress.done}/{batchProgress.total}
            </div>
            <ProgressBar now={(batchProgress.done / Math.max(1, batchProgress.total)) * 100} style={{ height: 8 }} />
          </div>
        )}

        {batchResults.length > 0 && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 8, letterSpacing: 0.4, textTransform: "uppercase" }}>
              Results ({batchResults.length})
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 10,
            }}>
              {batchResults.map((r, i) => (
                <div key={r.url} style={{ border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff", overflow: "hidden" }}>
                  <div style={{ aspectRatio: "9 / 16", background: "#0f172a" }}>
                    <video
                      src={r.url}
                      controls
                      preload="metadata"
                      playsInline
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  </div>
                  <div style={{ padding: "6px 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, color: "#64748b" }}>#{i + 1}</span>
                    <a
                      href={r.url}
                      download={`reel-variation-${i + 1}.${r.ext || "webm"}`}
                      style={{ fontSize: 11, color: "var(--color-accent-active)", textDecoration: "none", fontWeight: 600 }}
                    >
                      ⬇ {(r.ext || "webm").toUpperCase()}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  handleSelectOption = (selectionIndex: number, optionIndex: number) => {
    this.setState((prev) => {
      const nextSelections = [...prev.brollSelections];
      if (!nextSelections[selectionIndex]) return prev;
      nextSelections[selectionIndex] = {
        ...nextSelections[selectionIndex],
        selectedIndex: optionIndex,
      };
      return { ...prev, brollSelections: nextSelections };
    });
  };

  handleSearchInputChange = (selectionIndex: number, value: string) => {
    this.setState((prev) => {
      const next = [...prev.searchInputs];
      next[selectionIndex] = value;
      return { ...prev, searchInputs: next };
    });
  };

  handleSearchForSelection = async (selectionIndex: number) => {
    const query = (this.state.searchInputs[selectionIndex] || "").trim();
    if (!query) return;

    this.setState({ searchLoadingIndex: selectionIndex });
    try {
      let options = await this.searchVideos(query);
      if (options.length === 0) {
        options = await this.searchVideos("peaceful nature");
      }

      if (options.length === 0) {
        this.setState({ reelProgress: "No videos found for that tag. Try another search." });
        return;
      }

      this.setState((prev) => {
        const nextSelections = [...prev.brollSelections];
        if (!nextSelections[selectionIndex]) return prev;
        nextSelections[selectionIndex] = {
          ...nextSelections[selectionIndex],
          keyword: query,
          options,
          selectedIndex: 0,
        };
        return { ...prev, brollSelections: nextSelections };
      });
    } catch (error) {
      console.error("Error searching videos for selection", error);
      this.setState({ reelProgress: "Error searching videos. Try again." });
    } finally {
      this.setState({ searchLoadingIndex: null });
    }
  };

  handleUploadForSelection = (selectionIndex: number, file: File | null) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const option: BrollOption = {
      videoUrl: url,
      thumbnail: "",
      duration: 5, // unknown; placeholder
      label: file.name,
      isUploaded: true,
    };

    this.setState((prev) => {
      const nextSelections = [...prev.brollSelections];
      if (!nextSelections[selectionIndex]) return prev;
      nextSelections[selectionIndex] = {
        ...nextSelections[selectionIndex],
        options: [option, ...nextSelections[selectionIndex].options],
        selectedIndex: 0,
      };
      return { ...prev, brollSelections: nextSelections };
    });
  };

  renderReel = async () => {
    const { audioFiles } = this.props;
    const { brollSelections } = this.state;
    const BROLL_DURATION = 5;

    if (brollSelections.length === 0) {
      alert("Generate and review B-rolls first.");
      return;
    }

    // Background music lookup disabled by request: render using selected clips + voice audio only.
    this.setState({ isRenderingReel: true, reelProgress: "Preparing reel...", progressPercent: 5, currentStep: "rendering", backgroundMusicUrl: null });

    try {
      this.setState({ reelProgress: "Loading community info...", progressPercent: 8 });
      const communityName = await this.loadCommunityName();

      this.setState({ reelProgress: "Rendering reel...", progressPercent: 10 });
      const selectedOptions = brollSelections.map((b) => b.options[b.selectedIndex]);
      const videoUrls = selectedOptions.map((opt) => opt?.videoUrl).filter(Boolean) as string[];
      const aiFlags = selectedOptions.map((opt) => opt?.isAiGenerated || false);

      if (videoUrls.length !== brollSelections.length) {
        this.setState({ reelProgress: "Some selections are missing videos. Please choose another option.", currentStep: "selecting" });
        this.setState({ isRenderingReel: false });
        return;
      }

      const renderedVideoUrl = await this.renderVideoClientSide(
        videoUrls,
        audioFiles,
        BROLL_DURATION,
        null,
        communityName,
        aiFlags
      );

      this.setState({
        renderedVideoUrl,
        reelProgress: "Reel generated successfully!",
        progressPercent: 100,
        hasConfirmedBrolls: true,
        currentStep: "complete",
      });
    } catch (error) {
      console.error("Error rendering reel:", error);
      this.setState({ reelProgress: "Error rendering reel. Please try again.", currentStep: "selecting" });
      const msg = error instanceof Error ? error.message : "Error rendering reel. Please try again.";
      alert(msg);
    } finally {
      this.setState({ isRenderingReel: false });
    }
  };

  getStepStatus = (step: WorkflowStep): "inactive" | "active" | "complete" => {
    const { currentStep } = this.state;
    const stepOrder: WorkflowStep[] = ["idle", "finding", "selecting", "rendering", "complete"];
    const currentIndex = stepOrder.indexOf(currentStep);
    const stepIndex = stepOrder.indexOf(step);

    if (stepIndex < currentIndex) return "complete";
    if (stepIndex === currentIndex) return "active";
    return "inactive";
  };

  getStepLineWidth = (): string => {
    const { currentStep } = this.state;
    switch (currentStep) {
      case "idle": return "0%";
      case "finding": return "25%";
      case "selecting": return "50%";
      case "rendering": return "75%";
      case "complete": return "100%";
      default: return "0%";
    }
  };

  renderStepCircle = (step: WorkflowStep, number: number, label: string) => {
    const status = this.getStepStatus(step);
    const circleStyle = {
      ...styles.stepCircle,
      ...(status === "inactive" ? styles.stepCircleInactive : {}),
      ...(status === "active" ? styles.stepCircleActive : {}),
      ...(status === "complete" ? styles.stepCircleComplete : {}),
    };
    const labelStyle = {
      ...styles.stepLabel,
      ...(status === "active" ? styles.stepLabelActive : {}),
    };

    return (
      <div style={styles.step}>
        <div style={circleStyle}>
          {status === "complete" ? "✓" : number}
        </div>
        <div style={labelStyle}>{label}</div>
      </div>
    );
  };

  render() {
    const { audioFiles, videoURL } = this.props;
    const {
      isGeneratingReel,
      isRenderingReel,
      isGeneratingImages,
      reelProgress,
      progressPercent,
      brollSelections,
      renderedVideoUrl,
      cloudVideoUrl,
      isConvertingToMp4,
      currentStep,
    } = this.state;

    const hasAudioFiles = audioFiles.length > 0;
    const isProcessing = isGeneratingReel || isRenderingReel || isConvertingToMp4 || isGeneratingImages;

    // Determine which video URL to display: prefer cloud URL after upload, then rendered local URL
    const displayVideoUrl = cloudVideoUrl || renderedVideoUrl;

    // Check if there's an existing video from the database that we should show
    const { isCreatingNewReel } = this.state;
    const hasExistingVideo = !!videoURL && currentStep === "idle" && !displayVideoUrl && !isCreatingNewReel;

    return (
      <div style={styles.container}>
        <div style={styles.innerContainer}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerIcon}>
              <span role="img" aria-label="video">🎬</span>
            </div>
            <div style={styles.headerText}>
              <h5 style={styles.title}>Stock Footage Reel Generator</h5>
              <p style={styles.subtitle}>
                Create professional reels with AI-matched stock footage
              </p>
            </div>
          </div>

          {/* Step Indicator */}
          {currentStep !== "idle" && this.state.reelMode !== "freeflow" && (
            <div style={styles.stepIndicator}>
              <div style={styles.stepLine} />
              <div style={{ ...styles.stepLineActive, width: this.getStepLineWidth() }} />
              {this.renderStepCircle("finding", 1, "Find B-rolls")}
              {this.renderStepCircle("selecting", 2, "Select clips")}
              {this.renderStepCircle("rendering", 3, "Render")}
              {this.renderStepCircle("complete", 4, "Done")}
            </div>
          )}

          {/* Existing Video Preview */}
          {hasExistingVideo && (
            <div style={styles.previewSection}>
              <div style={styles.previewHeader}>
                <div style={styles.previewTitle}>
                  <span role="img" aria-label="video" style={{ fontSize: 24 }}>🎥</span>
                  Existing Reel
                </div>
              </div>

              <div style={styles.previewContent}>
                <video
                  src={videoURL}
                  controls
                  style={styles.previewVideo}
                />

                <div style={styles.previewInfo}>
                  <div style={styles.previewInfoItem}>
                    <div style={styles.previewInfoIcon}>
                      <span role="img" aria-label="cloud">☁️</span>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: "var(--color-surface-inset)" }}>Saved to Cloud</div>
                      <div style={{ fontSize: 13, color: "#6b7280" }}>This reel is already uploaded</div>
                    </div>
                  </div>
                  <div style={styles.previewInfoItem}>
                    <div style={styles.previewInfoIcon}>
                      <span role="img" aria-label="format">📱</span>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: "var(--color-surface-inset)" }}>9:16 Portrait</div>
                      <div style={{ fontSize: 13, color: "#6b7280" }}>Perfect for Reels & TikTok</div>
                    </div>
                  </div>

                  <div style={{ marginTop: 20, display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <Button
                      style={styles.successButton}
                      onClick={() => {
                        const a = document.createElement("a");
                        a.href = videoURL!;
                        a.download = `reel-${Date.now()}.mp4`;
                        a.target = "_blank";
                        a.click();
                      }}
                    >
                      <span role="img" aria-label="download">⬇️</span>
                      Download MP4
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={this.downloadCombinedAudio}
                      disabled={audioFiles.length === 0 || isConvertingToMp4}
                      style={{ borderRadius: 10, padding: "12px 24px" }}
                    >
                      <span role="img" aria-label="audio">🎵</span>
                      {audioFiles.length > 1 ? "Download Combined Audio" : "Download Audio"}
                    </Button>
                    <Button
                      variant="outline-primary"
                      onClick={() => this.setState({ isCreatingNewReel: true, cloudVideoUrl: null, renderedVideoUrl: null })}
                      style={{ borderRadius: 10, padding: "12px 24px" }}
                    >
                      Create New Reel
                    </Button>
                  </div>
                  <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8, marginBottom: 0 }}>
                    Creating a new reel will replace the existing one.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Initial State - Ready to Start */}
          {currentStep === "idle" && !hasExistingVideo && (
            <div style={{ padding: "20px 0" }}>
              {/* Mode Toggle */}
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 8, display: "block" }}>
                  Creation Mode
                </label>
                <div style={{ display: "inline-flex", borderRadius: 10, overflow: "hidden", border: "1px solid #e5e7eb" }}>
                  <button
                    type="button"
                    onClick={() => this.setState({ reelMode: "auto" })}
                    style={{
                      padding: "10px 20px",
                      border: "none",
                      background: this.state.reelMode === "auto"
                        ? "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)"
                        : "#fff",
                      color: this.state.reelMode === "auto" ? "#fff" : "#374151",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    Auto Generate
                  </button>
                  <button
                    type="button"
                    onClick={() => this.setState({ reelMode: "freeflow" })}
                    style={{
                      padding: "10px 20px",
                      border: "none",
                      borderLeft: "1px solid #e5e7eb",
                      background: this.state.reelMode === "freeflow"
                        ? "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)"
                        : "#fff",
                      color: this.state.reelMode === "freeflow" ? "#fff" : "#374151",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    Free Flow
                  </button>
                </div>
              </div>

              {/* Auto Generate Mode */}
              {this.state.reelMode === "auto" && hasAudioFiles && (
                <div style={{ textAlign: "center" }}>
                  {/* Video Source Selector */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 8, display: "block" }}>
                      Video Source
                    </label>
                    <div style={{ display: "inline-flex", borderRadius: 10, overflow: "hidden", border: "1px solid #e5e7eb" }}>
                      <button
                        type="button"
                        onClick={() => this.setState({ videoSource: "pexels" })}
                        style={{
                          padding: "10px 20px",
                          border: "none",
                          background: this.state.videoSource === "pexels"
                            ? "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)"
                            : "#fff",
                          color: this.state.videoSource === "pexels" ? "#fff" : "#374151",
                          fontWeight: 600,
                          fontSize: 14,
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                      >
                        Pexels
                      </button>
                      <button
                        type="button"
                        onClick={() => this.setState({ videoSource: "pixabay" })}
                        style={{
                          padding: "10px 20px",
                          border: "none",
                          borderLeft: "1px solid #e5e7eb",
                          background: this.state.videoSource === "pixabay"
                            ? "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)"
                            : "#fff",
                          color: this.state.videoSource === "pixabay" ? "#fff" : "#374151",
                          fontWeight: 600,
                          fontSize: 14,
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                      >
                        Pixabay
                      </button>
                    </div>
                  </div>

                  <Button
                    style={styles.primaryButton}
                    onClick={this.generateStockFootageReel}
                    disabled={isGeneratingReel}
                  >
                    {isGeneratingReel ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        Finding B-rolls...
                      </>
                    ) : (
                      <>
                        <span role="img" aria-label="search">🔍</span>
                        Find Stock Footage
                      </>
                    )}
                  </Button>
                  <p style={{ marginTop: 12, fontSize: 13, color: "#6b7280" }}>
                    AI will analyze your content and find matching stock videos from {this.state.videoSource === "pexels" ? "Pexels" : "Pixabay"}
                  </p>
                </div>
              )}

              {/* Auto Generate Mode - No Audio */}
              {this.state.reelMode === "auto" && !hasAudioFiles && (
                <div style={styles.emptyState}>
                  <div style={styles.emptyStateIcon}>🎵</div>
                  <div style={styles.emptyStateTitle}>Audio Required</div>
                  <p style={styles.emptyStateText}>
                    Generate audio files first to create a stock footage reel.
                  </p>
                </div>
              )}

              {/* Free Flow Mode */}
              {this.state.reelMode === "freeflow" && this.renderFreeFlowComposer()}

            </div>
          )}

          {/* Progress Card */}
          {isProcessing && reelProgress && (
            <div style={styles.progressCard}>
              <div style={styles.progressText}>
                <Spinner animation="border" size="sm" variant="primary" />
                {reelProgress}
              </div>
              <ProgressBar
                now={progressPercent}
                variant="primary"
                animated
                style={{ height: 8, borderRadius: 4 }}
              />
            </div>
          )}

          {/* B-roll Selection Grid */}
          {brollSelections.length > 0 && currentStep === "selecting" && (
            <>
              <div style={{ marginBottom: 16 }}>
                <h6 style={{ fontSize: 15, fontWeight: 600, color: "var(--color-surface-inset)", marginBottom: 4 }}>
                  Select B-roll for each segment
                </h6>
                <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
                  Click on a thumbnail to select it. You can also search, upload your own clip, or generate AI images.
                </p>
                <div style={{ marginTop: 10 }}>
                  <Button
                    variant="outline-warning"
                    onClick={this.handleGenerateAiForAllSegments}
                    disabled={isRenderingReel || isGeneratingImages || this.state.aiGeneratingIndex !== null}
                    style={{ borderRadius: 10 }}
                  >
                    {isGeneratingImages ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        Generating AI For All...
                      </>
                    ) : (
                      <>
                        <span role="img" aria-label="batch-ai">✨</span>
                        Generate AI For All Segments
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div style={styles.brollGrid}>
                {brollSelections.map((broll, idx) => (
                  <div
                    key={`${broll.keyword}-${idx}`}
                    style={{
                      ...styles.brollCard,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    {/* Card Header */}
                    <div style={styles.brollCardHeader}>
                      <div style={styles.brollCardHeaderTitle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={styles.brollNumber}>{idx + 1}</span>
                          <span style={styles.brollTag}>{broll.keyword || "n/a"}</span>
                        </div>
                        <span style={{ fontSize: 12, color: "#9ca3af" }}>5s segment</span>
                      </div>
                      <div style={styles.brollTextPreview}>
                        {broll.textSegment || "—"}
                      </div>
                    </div>

                    {/* Card Body */}
                    <div style={styles.brollCardBody}>
                      {/* Horizontal Thumbnail Carousel */}
                      <div style={styles.thumbnailCarousel}>
                        {broll.options.map((opt, optIdx) => {
                          const isSelected = optIdx === broll.selectedIndex;
                          return (
                            <div
                              key={`${opt.video?.id || "upload"}-${optIdx}`}
                              style={{
                                ...styles.thumbnailItem,
                                ...(isSelected ? styles.thumbnailItemSelected : {}),
                              }}
                              onClick={() => this.handleSelectOption(idx, optIdx)}
                            >
                              {opt.isAiGenerated ? (
                                <img
                                  src={opt.videoUrl}
                                  alt={opt.label || "AI Generated"}
                                  style={styles.thumbnailVideo}
                                />
                              ) : (
                                <video
                                  src={opt.videoUrl}
                                  muted
                                  playsInline
                                  loop
                                  style={styles.thumbnailVideo}
                                  onMouseEnter={(e) => (e.currentTarget as HTMLVideoElement).play()}
                                  onMouseLeave={(e) => {
                                    const vid = e.currentTarget as HTMLVideoElement;
                                    vid.pause();
                                    vid.currentTime = 0;
                                  }}
                                />
                              )}
                              <div style={styles.thumbnailLabel}>
                                {isSelected ? "✓ Selected" : opt.isAiGenerated ? "AI" : `${Math.round(opt.duration)}s`}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Search Row */}
                      <div style={styles.searchRow}>
                        {/* Compact Source Toggle */}
                        <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", border: "1px solid #e5e7eb", flexShrink: 0 }}>
                          <button
                            type="button"
                            onClick={() => this.setState({ videoSource: "pexels" })}
                            style={{
                              padding: "6px 10px",
                              border: "none",
                              background: this.state.videoSource === "pexels" ? "var(--color-accent)" : "#fff",
                              color: this.state.videoSource === "pexels" ? "#fff" : "#6b7280",
                              fontWeight: 500,
                              fontSize: 11,
                              cursor: "pointer",
                            }}
                          >
                            Pexels
                          </button>
                          <button
                            type="button"
                            onClick={() => this.setState({ videoSource: "pixabay" })}
                            style={{
                              padding: "6px 10px",
                              border: "none",
                              borderLeft: "1px solid #e5e7eb",
                              background: this.state.videoSource === "pixabay" ? "var(--color-accent)" : "#fff",
                              color: this.state.videoSource === "pixabay" ? "#fff" : "#6b7280",
                              fontWeight: 500,
                              fontSize: 11,
                              cursor: "pointer",
                            }}
                          >
                            Pixabay
                          </button>
                        </div>
                        <input
                          type="text"
                          value={this.state.searchInputs[idx] ?? broll.keyword ?? ""}
                          onChange={(e) => this.handleSearchInputChange(idx, e.target.value)}
                          placeholder="Search different clips..."
                          style={styles.searchInput}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = "var(--color-accent)";
                            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.1)";
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = "#d1d5db";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        />
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => this.handleSearchForSelection(idx)}
                          disabled={this.state.searchLoadingIndex === idx || isRenderingReel}
                          style={{ borderRadius: 8, flexShrink: 0, whiteSpace: "nowrap" }}
                        >
                          {this.state.searchLoadingIndex === idx ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            "Search"
                          )}
                        </Button>
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 6 }}>
                          AI image prompt
                        </label>
                        <textarea
                          value={this.state.aiPromptInputs[idx] ?? broll.textSegment ?? ""}
                          onChange={(e) => this.handleAiPromptChange(idx, e.target.value)}
                          placeholder="Auto-generated from community visual/writing style (editable)"
                          style={styles.promptTextarea}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = "#f59e0b";
                            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(245, 158, 11, 0.15)";
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = "#d1d5db";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        />
                      </div>

                      {/* Upload & AI Generate Options */}
                      <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                        <label
                          style={{ ...styles.uploadLabel, flex: 1 }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f1f5f9";
                            e.currentTarget.style.borderColor = "#94a3b8";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#f8fafc";
                            e.currentTarget.style.borderColor = "#cbd5e1";
                          }}
                        >
                          <span role="img" aria-label="upload">📤</span>
                          Upload your own
                          <input
                            type="file"
                            accept="video/*"
                            style={{ display: "none" }}
                            onChange={(e) => this.handleUploadForSelection(idx, e.target.files?.[0] || null)}
                            disabled={isRenderingReel}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => this.handleAiGenerateForSelection(idx)}
                          disabled={
                            this.state.aiGeneratingIndex !== null
                            || isGeneratingImages
                            || isRenderingReel
                          }
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            padding: "8px 12px",
                            borderRadius: 8,
                            border: "1px dashed #d97706",
                            background: this.state.aiGeneratingIndex === idx ? "#fef3c7" : "#fffbeb",
                            color: "#92400e",
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: (this.state.aiGeneratingIndex !== null || isGeneratingImages)
                              ? "not-allowed"
                              : "pointer",
                            transition: "all 0.2s",
                          }}
                        >
                          {this.state.aiGeneratingIndex === idx ? (
                            <>
                              <Spinner animation="border" size="sm" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <span role="img" aria-label="ai">✨</span>
                              AI Generate
                            </>
                          )}
                        </button>
                      </div>

                      {/* AI Image Settings */}
                      <div style={{ marginTop: 8, display: "flex", gap: 6, alignItems: "center" }}>
                        <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", border: "1px solid #e5e7eb", flexShrink: 0 }}>
                          <button
                            type="button"
                            onClick={() => this.setState({ imageProvider: "nanobanana" })}
                            style={{
                              padding: "4px 8px",
                              border: "none",
                              background: this.state.imageProvider === "nanobanana" ? "#f59e0b" : "#fff",
                              color: this.state.imageProvider === "nanobanana" ? "#fff" : "#6b7280",
                              fontWeight: 500,
                              fontSize: 10,
                              cursor: "pointer",
                            }}
                          >
                            Gemini
                          </button>
                          <button
                            type="button"
                            onClick={() => this.setState({ imageProvider: "dalle" })}
                            style={{
                              padding: "4px 8px",
                              border: "none",
                              borderLeft: "1px solid #e5e7eb",
                              background: this.state.imageProvider === "dalle" ? "#10a37f" : "#fff",
                              color: this.state.imageProvider === "dalle" ? "#fff" : "#6b7280",
                              fontWeight: 500,
                              fontSize: 10,
                              cursor: "pointer",
                            }}
                          >
                            GPT
                          </button>
                        </div>
                        <input
                          type="text"
                          value={this.state.nanoBananaTheme}
                          onChange={(e) => this.setState({ nanoBananaTheme: e.target.value })}
                          placeholder="Image theme (optional)"
                          style={{
                            flex: 1,
                            padding: "4px 8px",
                            borderRadius: 6,
                            border: "1px solid #e5e7eb",
                            fontSize: 11,
                            outline: "none",
                          }}
                        />
                      </div>

                      {broll.options[broll.selectedIndex]?.isAiGenerated && (
                        <div style={{ marginTop: 10 }}>
                          <Button
                            size="sm"
                            variant="outline-warning"
                            onClick={() => this.downloadImage(
                              broll.options[broll.selectedIndex].videoUrl,
                              `ai-image-segment-${idx + 1}-${Date.now()}.png`
                            )}
                            style={{ borderRadius: 8 }}
                          >
                            <span role="img" aria-label="download-image">🖼️</span>
                            Download Generated Image
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Bar */}
              <div style={styles.actionBar}>
                <Button
                  style={styles.successButton}
                  onClick={this.renderReel}
                  disabled={isRenderingReel || isGeneratingImages}
                >
                  {isRenderingReel ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      Rendering...
                    </>
                  ) : (
                    <>
                      <span role="img" aria-label="render">🎥</span>
                      Render Reel
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={this.handleClear}
                  disabled={isRenderingReel || isGeneratingImages}
                  style={{ borderRadius: 10, padding: "12px 24px" }}
                >
                  Start Over
                </Button>
              </div>
            </>
          )}

          {/* Final Preview */}
          {displayVideoUrl && currentStep === "complete" && (
            <div style={styles.previewSection}>
              <div style={styles.previewHeader}>
                <div style={styles.previewTitle}>
                  <span role="img" aria-label="success" style={{ fontSize: 24 }}>✅</span>
                  Your Reel is Ready
                  {cloudVideoUrl && (
                    <span style={{ marginLeft: 12, fontSize: 12, background: "#d1fae5", color: "#065f46", padding: "4px 10px", borderRadius: 20, fontWeight: 600 }}>
                      ☁️ Saved to Cloud
                    </span>
                  )}
                </div>
              </div>

              <div style={styles.previewContent}>
                <video
                  src={displayVideoUrl}
                  controls
                  style={styles.previewVideo}
                />

                <div style={styles.previewInfo}>
                  <div style={styles.previewInfoItem}>
                    <div style={styles.previewInfoIcon}>
                      <span role="img" aria-label="clips">🎞️</span>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: "var(--color-surface-inset)" }}>
                        {this.state.reelMode === "freeflow"
                          ? `${this.state.freeFlowSegments.length} segments`
                          : `${brollSelections.length} B-roll clips`}
                      </div>
                      <div style={{ fontSize: 13, color: "#6b7280" }}>
                        {this.state.reelMode === "freeflow" ? "Free flow composition" : "AI-matched to your content"}
                      </div>
                    </div>
                  </div>
                  {this.state.reelMode !== "freeflow" && (
                    <div style={styles.previewInfoItem}>
                      <div style={styles.previewInfoIcon}>
                        <span role="img" aria-label="audio">🔊</span>
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: "var(--color-surface-inset)" }}>{audioFiles.length} audio tracks</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Synced with selected clips</div>
                      </div>
                    </div>
                  )}
                  <div style={styles.previewInfoItem}>
                    <div style={styles.previewInfoIcon}>
                      <span role="img" aria-label="format">📱</span>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: "var(--color-surface-inset)" }}>9:16 Portrait</div>
                      <div style={{ fontSize: 13, color: "#6b7280" }}>Perfect for Reels & TikTok</div>
                    </div>
                  </div>

                  {cloudVideoUrl && (
                    <div style={styles.previewInfoItem}>
                      <div style={{ ...styles.previewInfoIcon, background: "#d1fae5" }}>
                        <span role="img" aria-label="cloud">☁️</span>
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: "var(--color-surface-inset)" }}>Uploaded to Cloud</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Video is saved and accessible</div>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 20 }}>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                      <Button
                        style={styles.successButton}
                        onClick={this.downloadReel}
                        disabled={isConvertingToMp4}
                      >
                        {isConvertingToMp4 ? (
                          <>
                            <Spinner animation="border" size="sm" />
                            Converting...
                          </>
                        ) : (
                          <>
                            <span role="img" aria-label="download">⬇️</span>
                            Download MP4
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline-primary"
                        onClick={this.downloadReelWebM}
                        disabled={isConvertingToMp4}
                        style={{ borderRadius: 10, padding: "12px 24px" }}
                      >
                        <span role="img" aria-label="fast">⚡</span> Quick Download (WebM)
                      </Button>
                      {this.state.reelMode !== "freeflow" && (
                        <Button
                          variant="outline-secondary"
                          onClick={this.downloadCombinedAudio}
                          disabled={audioFiles.length === 0 || isConvertingToMp4}
                          style={{ borderRadius: 10, padding: "12px 24px" }}
                        >
                          <span role="img" aria-label="audio">🎵</span>
                          {audioFiles.length > 1 ? "Download Combined Audio" : "Download Audio"}
                        </Button>
                      )}
                      {this.state.reelMode === "freeflow" && (
                        <Button
                          variant="outline-primary"
                          onClick={() => {
                            if (this.state.renderedVideoUrl) URL.revokeObjectURL(this.state.renderedVideoUrl);
                            this.setState({
                              renderedVideoUrl: null,
                              cloudVideoUrl: null,
                              currentStep: "idle",
                              freeFlowCurrentStep: "composing",
                              reelProgress: "",
                              progressPercent: 0,
                            });
                          }}
                          style={{ borderRadius: 10, padding: "12px 24px" }}
                        >
                          Edit Segments
                        </Button>
                      )}
                      <Button
                        variant="outline-secondary"
                        onClick={this.handleClear}
                        style={{ borderRadius: 10, padding: "12px 24px" }}
                      >
                        Create Another
                      </Button>
                    </div>
                    <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8, marginBottom: 0 }}>
                      MP4 is more compatible but requires conversion. WebM downloads instantly.
                    </p>
                  </div>
                </div>
              </div>

              {/* Conversion Progress */}
              {isConvertingToMp4 && reelProgress && (
                <div style={{ ...styles.progressCard, marginTop: 20 }}>
                  <div style={styles.progressText}>
                    <Spinner animation="border" size="sm" variant="primary" />
                    {reelProgress}
                  </div>
                  <ProgressBar
                    now={progressPercent}
                    variant="success"
                    animated
                    style={{ height: 8, borderRadius: 4 }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default StockFootageReelGenerator;
